import React from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  FaDrawPolygon,
  FaSave,
} from 'react-icons/fa';
import { featureService } from '../../features/services/featureService';
import { toolkitService } from '../services/toolkitService';
import { useToast } from '../../../components/ui/ToastProvider';
import { useHighlightGroup } from '../../map/context/HighlightContext';
import api from '../../../utils/api';
import { clubFields } from '../../map/utils/clubBoundaryRole';
import { YButton } from '../../../ui/YButton';
import { PlantingPlansLibrary } from './PlantingPlansLibrary';
import { PlantingMarkPlantedDialog } from './PlantingMarkPlantedDialog';
import { PlantingShareDialog } from './PlantingShareDialog';
import { PlantingMissingPlanDialog } from './PlantingMissingPlanDialog';
import { PlantingDeleteLogDialog } from './PlantingDeleteLogDialog';
import PlantingLibraryStrips from './PlantingLibraryStrips';
import { PlantingPlanFlow } from './PlantingPlanFlow';
import { PlantingGroundStep } from './PlantingGroundStep';
import { PlantingWizardStep } from './PlantingWizardStep';
import { PlantingRevealStep } from './PlantingRevealStep';
import { PlantingPlanStep } from './PlantingPlanStep';
import { PlantingTimingStep } from './PlantingTimingStep';
import { PlantingGrowthStep } from './PlantingGrowthStep';
import { PlantingPlanStepStrip } from './PlantingPlanStepStrip';
import { usePlantingPlansLibraryState } from './usePlantingPlansLibraryState';
import { usePlantingPlanFlowState } from './usePlantingPlanFlowState';
import {
  continueLabelForStep,
  nextPlantingFlowStep,
  normalizePlantingFlowStep,
  plantingFlowKicker,
  usesFieldEditionPlanStep,
} from './plantingFlowUtils';
import {
  buildAreaGroupMetadataPayload,
  buildGroundPlaceFromSelection,
  buildConfirmedGroundPlace,
  buildPlantingPlanNote,
  formatGroundAcresInput,
  getPlotCentroid,
  hasGroundPlaceCoords,
  hasPlanName,
  isGroundReady,
  isGroundStepReady,
  normalizeUsdaZoneCode,
  parseGroundAcres,
  pickPrimaryPlot,
  resolveGroundForecastPoint,
  resolveSoilLookupState,
  soilSurveyFromLookup,
  soilTypePresetLabel,
} from './plantingGroundUtils';
import {
  frostLabelsForZone,
  frostLabelsFromZoneResult,
  shouldApplyAutofill,
} from './plantingGroundOverrideUtils';
import {
  EMPTY_WIZARD_ANSWERS,
  advanceWizardIndex,
  applyWizardAnswer,
  canAdvanceWizard,
  hydrateWizardAnswers,
  sanitizeWizardAnswers,
  wizardContinueLabel,
  wizardIndexForHydratedAnswers,
} from './plantingWizardUtils';
import {
  REVEAL_STAGE_MAX,
  revealContinueLabel,
} from './plantingRevealUtils';
import {
  blendKeyFromAnswers,
  catalogBlendDisplayName,
  planPreviewRequest,
  sanitizeBlendKey,
} from './plantingPlanDisplay';
import { resolvePlanAcres, stripPlanNameAcres } from './plantingPlansLibraryUtils';
import {
  clubNameMap,
  shareSentToast,
  shareUnsharedToast,
} from './plantingPlanShare';
import { findLivePlanForEvent } from './plantingLogUtils';
import { getPlantingSchedule } from './plantingTimingUtils';
import { growthContinueLabel, typicalPlantLabel } from './plantingGrowthUtils';
import {
  buildPlantingExportPayload,
  buildFieldEditionExportPayload,
  canShowPlantingExport,
  formatUserDisplayName,
  resolveGroupPlotsForExport,
  resolveOptionLabel,
} from './plantingExportUtils';
import { downloadPlantingEstimatePdf } from './downloadPlantingEstimatePdf';
import { ToolkitShellTabs } from '../components/ToolkitShellTabs';
import {
  TOOLKIT_PLANTING_PATH,
  plantingPlanLocation,
} from '../constants/toolkitRoutes';
import './planting-assistant-shell.css';

/* Iteration 1–2: setters come from useState inside library/flow hooks (stable). */
/* eslint-disable react-hooks/exhaustive-deps -- custom-hook setters; revisit when effects leave this page */

// TODO: expand to dynamic state detection; AL only for now.
const SOIL_LOOKUP_STATE = 'AL';

const formatAcreage = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  return `${value.toFixed(2)} ac`;
};

const formatLocation = (feature) => {
  try {
    const centroid = feature?.centroid;
    if (Array.isArray(centroid?.coordinates) && centroid.coordinates.length >= 2) {
      const [lng, lat] = centroid.coordinates;
      const latLabel = `${Math.abs(lat).toFixed(3)}° ${lat >= 0 ? 'N' : 'S'}`;
      const lngLabel = `${Math.abs(lng).toFixed(3)}° ${lng >= 0 ? 'E' : 'W'}`;
      return `${latLabel}, ${lngLabel}`;
    }
    if (typeof feature?.latitude === 'number' && typeof feature?.longitude === 'number') {
      const lat = feature.latitude;
      const lng = feature.longitude;
      const latLabel = `${Math.abs(lat).toFixed(3)}° ${lat >= 0 ? 'N' : 'S'}`;
      const lngLabel = `${Math.abs(lng).toFixed(3)}° ${lng >= 0 ? 'E' : 'W'}`;
      return `${latLabel}, ${lngLabel}`;
    }
  } catch (_) {
    // ignore
  }
  return '—';
};

const resolveClubName = (feature) => {
  return feature?.club?.name || feature?.shared_with_club?.name || feature?.club_name || null;
};

export function AreaCalculatorPage() {
  const { show } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setHighlightGroup } = useHighlightGroup();
  const library = usePlantingPlansLibraryState();
  const flow = usePlantingPlanFlowState();
  const {
    polygons,
    setPolygons,
    loading,
    setLoading,
    error,
    setError,
    savedGroups,
    setSavedGroups,
    groupsLoading,
    setGroupsLoading,
    groupError,
    setGroupError,
    deletingGroupId,
    setDeletingGroupId,
    editingGroupId,
    setEditingGroupId,
    mode,
    setMode,
    presidentClubs,
    setPresidentClubs,
    clubsLoading,
    setClubsLoading,
    selectedClubId,
    setSelectedClubId,
    clubPolygonsById,
    setClubPolygonsById,
    clubPolygonsLoading,
    setClubPolygonsLoading,
    clubPolygonsError,
    setClubPolygonsError,
  } = library;
  const {
    selectedIds,
    setSelectedIds,
    draftManualAcres,
    setDraftManualAcres,
    draftPlace,
    setDraftPlace,
    draftPlaceQuery,
    setDraftPlaceQuery,
    draftWizardAnswers,
    setDraftWizardAnswers,
    draftWizardIndex,
    setDraftWizardIndex,
    draftBlendKey,
    setDraftBlendKey,
    draftPlanPreview,
    setDraftPlanPreview,
    draftGroupName,
    setDraftGroupName,
    draftGroupNote,
    setDraftGroupNote,
    draftZoneSource,
    setDraftZoneSource,
    draftZoneCode,
    setDraftZoneCode,
    draftSoilType,
    setDraftSoilType,
    draftSoilTypeSource,
    setDraftSoilTypeSource,
    draftSoilDrainage,
    setDraftSoilDrainage,
    draftSoilDrainageSource,
    setDraftSoilDrainageSource,
    draftSoilPh,
    setDraftSoilPh,
    draftSoilPhSource,
    setDraftSoilPhSource,
    draftSoilNotes,
    setDraftSoilNotes,
    draftSoilSeries,
    setDraftSoilSeries,
    draftSoilDrainageRaw,
    setDraftSoilDrainageRaw,
    draftSoilSurveyCovered,
    setDraftSoilSurveyCovered,
    draftFrostLastLabel,
    setDraftFrostLastLabel,
    draftFrostFirstLabel,
    setDraftFrostFirstLabel,
    draftFrostSource,
    setDraftFrostSource,
    groundSurveyResetToken,
    setGroundSurveyResetToken,
    phHelperText,
    setPhHelperText,
    calculatorGroupId,
    setCalculatorGroupId,
    calculatorMode,
    setCalculatorMode,
    selectedSeedId,
    setSelectedSeedId,
    selectedFertilizerId,
    setSelectedFertilizerId,
    recommendationResult,
    setRecommendationResult,
    recommendationLoading,
    setRecommendationLoading,
    recommendationError,
    setRecommendationError,
    autoRecalcQueued,
    setAutoRecalcQueued,
    saveError,
    setSaveError,
    savingGroup,
    setSavingGroup,
    editingSaveLoading,
    setEditingSaveLoading,
    editingError,
    setEditingError,
    detectZoneLoading,
    setDetectZoneLoading,
    detectZoneError,
    setDetectZoneError,
    exportPdfLoading,
    setExportPdfLoading,
    groupOptions,
    setGroupOptions,
    optionsLoading,
    setOptionsLoading,
    optionsError,
    setOptionsError,
  } = flow;
  const previousSelectionRef = React.useRef(null);
  const hydratedPlanRef = React.useRef(null);
  const selectedIdsRef = React.useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const flowSessionRef = React.useRef(false);
  const groundZoneLookupSeqRef = React.useRef(0);
  const groundFieldSourcesRef = React.useRef({
    zone: '',
    soilType: '',
    soilDrainage: '',
    soilPh: '',
    frost: '',
  });
  groundFieldSourcesRef.current = {
    zone: draftZoneSource,
    soilType: draftSoilTypeSource,
    soilDrainage: draftSoilDrainageSource,
    soilPh: draftSoilPhSource,
    frost: draftFrostSource,
  };
  const normalizeZoneCode = normalizeUsdaZoneCode;
  const isEditing = calculatorMode === 'edit' && Boolean(editingGroupId);
  const [selectedPlanIds, setSelectedPlanIds] = React.useState(() => new Set());
  const [moreMenuGroupId, setMoreMenuGroupId] = React.useState(null);
  const [markPlantedGroup, setMarkPlantedGroup] = React.useState(null);
  const [shareGroup, setShareGroup] = React.useState(null);
  const [clubNames, setClubNames] = React.useState({});
  const [plantingEvents, setPlantingEvents] = React.useState([]);
  const [plantingEventsLoading, setPlantingEventsLoading] = React.useState(true);
  const [missingPlanEvent, setMissingPlanEvent] = React.useState(null);
  const [pendingDeleteEvent, setPendingDeleteEvent] = React.useState(null);
  const [deletingLogEvent, setDeletingLogEvent] = React.useState(false);
  const [deleteLogError, setDeleteLogError] = React.useState('');
  const [revealStage, setRevealStage] = React.useState(0);
  const [planSaved, setPlanSaved] = React.useState(false);
  const [planNameNeeded, setPlanNameNeeded] = React.useState(false);
  const [exportingPlanId, setExportingPlanId] = React.useState(null);

  const flowRouteMatch = React.useMemo(() => {
    const path = String(location.pathname || '').replace(/\/+$/, '');
    const match = path.match(/\/toolkit\/planting-assistant\/plan\/([^/]+)$/);
    if (!match) return { inFlow: false, planId: null };
    return { inFlow: true, planId: decodeURIComponent(match[1]) };
  }, [location.pathname]);
  const inFlow = flowRouteMatch.inFlow;
  const flowPlanKey = flowRouteMatch.planId;
  const isNewPlan = flowPlanKey === 'new';
  const flowStep = normalizePlantingFlowStep(searchParams.get('step'));
  const selectedClubName = React.useMemo(() => {
    if (!selectedClubId) return '';
    const club = presidentClubs.find((c) => c.id === selectedClubId);
    return club ? club.name : '';
  }, [presidentClubs, selectedClubId]);
  const selectionKey = React.useMemo(() => [...selectedIds].sort().join(','), [selectedIds]);
  const lastAutofillToastRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    const loadPolygons = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await featureService.getFeatures();
        if (cancelled) return;
        const filtered = (Array.isArray(response) ? response : [])
          .filter((feature) => {
            const type = feature?.geojson?.type || feature?.type;
            return type === 'Polygon' || type === 'MultiPolygon';
          })
          .map((feature) => ({
            id: String(feature.id),
            name: feature.name || 'Unnamed polygon',
            location: formatLocation(feature),
            club: resolveClubName(feature),
            createdAt: feature.created_at || feature.createdAt || null,
            acreage: typeof feature.acreage === 'number' ? feature.acreage : null,
            geojson: feature.geojson || feature.geometry || null,
            centroid: feature.centroid || null,
            raw: feature
          }));
        setPolygons(filtered);
        // Remove selections that no longer exist
        setSelectedIds((prev) => {
          const next = new Set();
          filtered.forEach((poly) => {
            if (prev.has(poly.id)) next.add(poly.id);
          });
          return next;
        });
      } catch (err) {
        console.error('Failed to load polygons for area calculator', err);
        if (!cancelled) setError('Unable to load polygons. Please refresh in a moment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadPolygons();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      setOptionsLoading(true);
      setOptionsError(null);
      try {
        const data = await toolkitService.getAreaGroupOptions();
        if (cancelled) return;
        setGroupOptions({
          zones: Array.isArray(data?.zones) ? data.zones : [],
          soilTypes: Array.isArray(data?.soil_types) ? data.soil_types : [],
          soilDrainage: Array.isArray(data?.soil_drainage) ? data.soil_drainage : [],
          soilPhGuidance: data?.soil_ph_guidance || {},
          soilNotePlaceholder: data?.soil_note_placeholder || '',
          seedPresets: Array.isArray(data?.seed_presets) ? data.seed_presets : [],
          fertilizerPresets: Array.isArray(data?.fertilizer_presets) ? data.fertilizer_presets : [],
          frostWindows: data?.frost_windows && typeof data.frost_windows === 'object'
            ? data.frost_windows
            : {},
        });
      } catch (err) {
        if (!cancelled) setOptionsError('Unable to load climate presets right now.');
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadGroups = React.useCallback(async () => {
    setGroupsLoading(true);
    setGroupError(null);
    try {
      const response = await toolkitService.getAreaGroups();
        const normalized = (Array.isArray(response) ? response : []).map((group) => ({
          ...group,
          personal_polygon_ids: (group.personal_polygon_ids || []).map((id) => String(id)),
          club_boundary_ids: (group.club_boundary_ids || []).map((id) => String(id)),
          total_acres: resolvePlanAcres(group),
        }));
        setSavedGroups(normalized);
    } catch (err) {
      console.error('Failed to load saved area groups', err);
      setGroupError('Unable to load saved area groups. Please try again in a bit.');
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const loadPlantingEvents = React.useCallback(async () => {
    setPlantingEventsLoading(true);
    try {
      const response = await toolkitService.getPlantingEvents();
      setPlantingEvents(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Failed to load planting events', err);
      setPlantingEvents([]);
    } finally {
      setPlantingEventsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadPlantingEvents();
  }, [loadPlantingEvents]);

  const ensureClubPolygons = React.useCallback(async (clubId) => {
    if (!clubId || clubPolygonsById[clubId]) return;
    setClubPolygonsLoading(true);
    setClubPolygonsError(null);
    try {
      const response = await api.get(`/clubs/${clubId}/`);
      const boundaries = clubFields(response?.data?.map?.hunt_boundaries || []);
      const formatted = boundaries.map((boundary) => ({
        id: String(boundary.id),
        name: boundary.name || 'Unnamed field',
        location: 'Club field',
        club: response?.data?.name || 'Club',
        createdAt: boundary.created,
        acreage: typeof boundary.acreage === 'number' ? boundary.acreage : null,
        geojson: boundary.geojson
      }));
      setClubPolygonsById((prev) => ({ ...prev, [clubId]: formatted }));
    } catch (err) {
      setClubPolygonsError('Unable to load club polygons right now.');
      throw err;
    } finally {
      setClubPolygonsLoading(false);
    }
  }, [clubPolygonsById]);
  React.useEffect(() => {
    if (mode !== 'club') return;
    if (selectedClubId || presidentClubs.length === 0) return;
    const firstClub = presidentClubs[0];
    if (firstClub) {
      setSelectedClubId(firstClub.id);
      ensureClubPolygons(firstClub.id).catch(() => {});
    }
  }, [mode, presidentClubs, selectedClubId, ensureClubPolygons]);

  React.useEffect(() => {
    if (mode === 'club' && !clubsLoading && presidentClubs.length === 0) {
      setMode('personal');
      setSelectedClubId(null);
      setSelectedIds(new Set());
    }
  }, [mode, clubsLoading, presidentClubs]);

  React.useEffect(() => {
    let cancelled = false;
    const loadClubs = async () => {
      setClubsLoading(true);
      try {
        const response = await api.get('/clubs/');
        if (cancelled) return;
        const clubs = Array.isArray(response?.data) ? response.data : [];
        setClubNames(clubNameMap(clubs));
        const filtered = clubs.filter((club) => club.allow_area_calculations && club.is_president);
        setPresidentClubs(filtered.map((club) => ({
          id: club.id,
          name: club.name,
          description: club.description || ''
        })));
      } catch (err) {
        if (!cancelled) {
          setPresidentClubs([]);
          setClubNames({});
        }
      } finally {
        if (!cancelled) setClubsLoading(false);
      }
    };
    loadClubs();
    return () => { cancelled = true; };
  }, []);

  const activePolygons = React.useMemo(() => {
    if (mode === 'personal') return polygons;
    if (!selectedClubId) return [];
    return clubPolygonsById[selectedClubId] || [];
  }, [mode, polygons, clubPolygonsById, selectedClubId]);

  const selectedPolygons = React.useMemo(() => {
    if (!activePolygons.length || selectedIds.size === 0) return [];
    return activePolygons.filter((poly) => selectedIds.has(poly.id));
  }, [activePolygons, selectedIds]);

  const selectedPlotTotal = React.useMemo(() => {
    return selectedPolygons.reduce((sum, poly) => sum + (poly.acreage || 0), 0);
  }, [selectedPolygons]);

  const selectedTotal = React.useMemo(() => {
    const manual = parseGroundAcres(draftManualAcres);
    if (manual > 0) return manual;
    return selectedPlotTotal;
  }, [draftManualAcres, selectedPlotTotal]);

  const handleGroundTogglePlot = React.useCallback((id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
    const selected = activePolygons.filter((poly) => next.has(poly.id));
    const total = selected.reduce((sum, poly) => sum + (poly.acreage || 0), 0);
    setDraftManualAcres(total > 0 ? formatGroundAcresInput(total) : '');
  }, [selectedIds, activePolygons, setSelectedIds, setDraftManualAcres]);

  const handleGroundMapSourceChange = React.useCallback((nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setSelectedIds(new Set());
    setDraftManualAcres('');
  }, [mode, setDraftManualAcres]);

  const handleGroundClubSelect = React.useCallback((rawId) => {
    const match = presidentClubs.find((club) => String(club.id) === String(rawId));
    const clubId = match ? match.id : rawId;
    if (String(clubId) === String(selectedClubId)) return;
    setSelectedClubId(clubId);
    setSelectedIds(new Set());
    setDraftManualAcres('');
    ensureClubPolygons(clubId).catch(() => {});
  }, [presidentClubs, selectedClubId, ensureClubPolygons, setDraftManualAcres]);

  const clearAutoGroundZone = React.useCallback(() => {
    setDraftZoneCode('');
    setDraftZoneSource('');
    setDetectZoneError(null);
    setDraftSoilSeries('');
    setDraftSoilDrainageRaw('');
    setDraftSoilSurveyCovered(false);
    setDraftSoilType('');
    setDraftSoilTypeSource('');
    setDraftSoilDrainage('');
    setDraftSoilDrainageSource('');
    setDraftSoilPh('');
    setDraftSoilPhSource('');
    setDraftFrostLastLabel('');
    setDraftFrostFirstLabel('');
    setDraftFrostSource('');
    setPhHelperText('');
  }, [
    setDraftZoneCode,
    setDraftZoneSource,
    setDetectZoneError,
    setDraftSoilSeries,
    setDraftSoilDrainageRaw,
    setDraftSoilSurveyCovered,
    setDraftSoilType,
    setDraftSoilTypeSource,
    setDraftSoilDrainage,
    setDraftSoilDrainageSource,
    setDraftSoilPh,
    setDraftSoilPhSource,
    setDraftFrostLastLabel,
    setDraftFrostFirstLabel,
    setDraftFrostSource,
    setPhHelperText,
  ]);

  const clearUncoveredSoilDraft = React.useCallback(() => {
    const sources = groundFieldSourcesRef.current;
    if (shouldApplyAutofill(sources.soilType)) {
      setDraftSoilType('');
      setDraftSoilSeries('');
    }
    if (shouldApplyAutofill(sources.soilDrainage)) {
      setDraftSoilDrainage('');
      setDraftSoilDrainageRaw('');
    }
    if (shouldApplyAutofill(sources.soilPh)) {
      setDraftSoilPh('');
      setPhHelperText('');
    }
    setDraftSoilSurveyCovered(false);
  }, [
    setDraftSoilType,
    setDraftSoilSeries,
    setDraftSoilDrainage,
    setDraftSoilDrainageRaw,
    setDraftSoilPh,
    setPhHelperText,
    setDraftSoilSurveyCovered,
  ]);

  const handleGroundResetToSurvey = React.useCallback(() => {
    setDraftZoneSource('');
    setDraftSoilTypeSource('');
    setDraftSoilDrainageSource('');
    setDraftSoilPhSource('');
    setDraftFrostSource('');
    setGroundSurveyResetToken((token) => token + 1);
  }, [
    setDraftZoneSource,
    setDraftSoilTypeSource,
    setDraftSoilDrainageSource,
    setDraftSoilPhSource,
    setDraftFrostSource,
    setGroundSurveyResetToken,
  ]);

  const handleGroundZoneOverride = React.useCallback((code) => {
    const next = normalizeUsdaZoneCode(code);
    setDraftZoneCode(next);
    setDraftZoneSource('manual');
    if (shouldApplyAutofill(groundFieldSourcesRef.current.frost)) {
      const frost = frostLabelsForZone(groupOptions.frostWindows, next);
      setDraftFrostLastLabel(frost.lastLabel);
      setDraftFrostFirstLabel(frost.firstLabel);
      setDraftFrostSource(frost.lastLabel || frost.firstLabel ? 'auto' : '');
    }
  }, [
    setDraftZoneCode,
    setDraftZoneSource,
    setDraftFrostLastLabel,
    setDraftFrostFirstLabel,
    setDraftFrostSource,
    groupOptions.frostWindows,
  ]);

  const handleGroundSoilPhOverride = React.useCallback((value) => {
    setDraftSoilPh(value);
    setDraftSoilPhSource('manual');
    setPhHelperText('Source: your soil kit / local knowledge.');
  }, [setDraftSoilPh, setDraftSoilPhSource, setPhHelperText]);

  const handleGroundFrostOverride = React.useCallback((lastLabel, firstLabel) => {
    setDraftFrostLastLabel(String(lastLabel || '').trim());
    setDraftFrostFirstLabel(String(firstLabel || '').trim());
    setDraftFrostSource('manual');
  }, [setDraftFrostLastLabel, setDraftFrostFirstLabel, setDraftFrostSource]);

  const handleGroundPlaceQueryChange = React.useCallback((query) => {
    setDraftPlaceQuery(query);
    const selectedLabel = draftPlace?.label || draftPlace?.name || '';
    if (draftPlace && query.trim() !== String(selectedLabel).trim()) {
      groundZoneLookupSeqRef.current += 1;
      setDraftPlace(null);
      clearAutoGroundZone();
    }
  }, [draftPlace, setDraftPlaceQuery, setDraftPlace, clearAutoGroundZone]);

  const handleGroundPlaceSelect = React.useCallback((place) => {
    if (!hasGroundPlaceCoords(place)) return;
    const label = String(place.label || place.name || '').trim();
    setDraftPlace(place);
    setDraftPlaceQuery(label);
  }, [setDraftPlace, setDraftPlaceQuery]);

  const seedOptions = React.useMemo(() => groupOptions.seedPresets || [], [groupOptions.seedPresets]);
  const fertilizerOptions = React.useMemo(() => groupOptions.fertilizerPresets || [], [groupOptions.fertilizerPresets]);

  const formatZoneOptionLabel = (zone) => {
    const base = zone.label || (zone.value ? `Zone ${String(zone.value).toUpperCase()}` : 'Zone');
    if (zone.min_temp_f === undefined && zone.max_temp_f === undefined) {
      return base;
    }
    const low = zone.min_temp_f !== undefined ? `${zone.min_temp_f}°F` : '—';
    const high = zone.max_temp_f !== undefined ? `${zone.max_temp_f}°F` : '—';
    return `${base} (${low} to ${high})`;
  };

  const buildMetadataPayload = buildAreaGroupMetadataPayload;

  const ensureZoneSelection = (currentValue) => {
    if (currentValue) return currentValue;
    const firstOption = (groupOptions.zones || [])[0];
    return firstOption ? firstOption.value : '';
  };

  React.useEffect(() => {
    if (optionsLoading) return;
    if (!selectedSeedId && seedOptions.length > 0) {
      setSelectedSeedId(seedOptions[0].id);
    }
    if (!selectedFertilizerId && fertilizerOptions.length > 0) {
      setSelectedFertilizerId(fertilizerOptions[0].id);
    }
  }, [optionsLoading, seedOptions, fertilizerOptions, selectedSeedId, selectedFertilizerId]);

  const clearRecommendations = React.useCallback(() => {
    setRecommendationResult(null);
    setRecommendationError(null);
    setAutoRecalcQueued(false);
  }, []);

  const finishNewGroupSave = React.useCallback((savedGroupId) => {
    setCalculatorGroupId(savedGroupId);
    setCalculatorMode('view');
    setEditingGroupId(null);
    setSaveError(null);
    navigate(plantingPlanLocation(savedGroupId, 'plan'), { replace: true, state: { flow: true } });
    flowSessionRef.current = true;
  }, [navigate]);

  const goToFlowStep = React.useCallback((step, { planId, replace = false } = {}) => {
    const targetId = planId != null
      ? planId
      : (isNewPlan ? 'new' : (flowPlanKey || calculatorGroupId || editingGroupId || 'new'));
    flowSessionRef.current = true;
    navigate(plantingPlanLocation(targetId, step), {
      replace,
      state: { flow: true },
    });
  }, [navigate, isNewPlan, flowPlanKey, calculatorGroupId, editingGroupId]);

  const resetFlowDraft = React.useCallback(() => {
    setCalculatorMode('compose');
    setEditingGroupId(null);
    setCalculatorGroupId(null);
    clearRecommendations();
    setSaveError(null);
    setEditingError(null);
    setDraftGroupName('');
    setDraftGroupNote('');
    setDraftManualAcres('');
    setDraftPlace(null);
    setDraftPlaceQuery('');
    setDraftWizardAnswers({ ...EMPTY_WIZARD_ANSWERS });
    setDraftWizardIndex(0);
    setDraftBlendKey('');
    setDraftPlanPreview(null);
    setRevealStage(0);
    setPlanSaved(false);
    setPlanNameNeeded(false);
    setDraftZoneSource('');
    setDraftZoneCode('');
    setDraftSoilType('');
    setDraftSoilTypeSource('');
    setDraftSoilDrainage('');
    setDraftSoilDrainageSource('');
    setDraftSoilPh('');
    setDraftSoilPhSource('');
    setDraftSoilNotes('');
    setDraftSoilSeries('');
    setDraftSoilDrainageRaw('');
    setDraftSoilSurveyCovered(false);
    setDraftFrostLastLabel('');
    setDraftFrostFirstLabel('');
    setDraftFrostSource('');
    setGroundSurveyResetToken(0);
    setPhHelperText('');
    setSelectedIds(new Set());
    setMode('personal');
    setSelectedClubId(null);
    setMoreMenuGroupId(null);
  }, [clearRecommendations]);

  const handleSeedSelect = (seedId) => {
    setSelectedSeedId(seedId);
    if (recommendationResult) {
      setAutoRecalcQueued(true);
    }
  };

  const handleFertilizerSelect = (fertilizerId) => {
    setSelectedFertilizerId(fertilizerId);
    if (recommendationResult) {
      setAutoRecalcQueued(true);
    }
  };

  const handleDraftManualToggle = (checked) => {
    if (checked) {
      setDraftZoneSource('manual');
      setDraftZoneCode((prev) => ensureZoneSelection(prev));
    } else {
      setDraftZoneSource('');
    }
    if (recommendationResult) {
      setAutoRecalcQueued(true);
    }
  };

  const handleCalculateRecommendations = React.useCallback(async (autoTrigger = false, groupIdOverride = null) => {
    setAutoRecalcQueued(false);
    const groupId = groupIdOverride || editingGroupId || calculatorGroupId;
    if (!groupId) {
      if (!autoTrigger) setRecommendationError('Save the group before requesting recommendations.');
      return;
    }
    if (!selectedSeedId && !selectedFertilizerId) {
      if (!autoTrigger) setRecommendationError('Select at least a seed or fertilizer preset.');
      return;
    }
    if (editingGroupId && draftZoneSource === 'manual' && !draftZoneCode) {
      if (!autoTrigger) setRecommendationError('Select a USDA zone when overriding manually.');
      return;
    }
    setRecommendationLoading(true);
    if (!autoTrigger) setRecommendationError(null);
    try {
      const payload = {};
      if (selectedSeedId) payload.seed_id = selectedSeedId;
      if (selectedFertilizerId) payload.fertilizer_id = selectedFertilizerId;
      const useDraftMetadata = groupId && (groupId === editingGroupId || groupId === calculatorGroupId);
      if (useDraftMetadata) {
        if (draftSoilType) payload.soil_type = draftSoilType;
        if (draftSoilDrainage) payload.soil_drainage = draftSoilDrainage;
        if (draftZoneSource) {
          payload.zone_source = draftZoneSource;
          if (draftZoneCode) payload.usda_zone_code = draftZoneCode;
        }
        if (draftSoilPh !== '') {
          const parsedPh = parseFloat(draftSoilPh);
          if (!Number.isNaN(parsedPh)) {
            payload.soil_ph = parsedPh;
          }
        }
      }
      const rec = await toolkitService.getAreaGroupRecommendations(groupId, payload);
      setRecommendationResult(rec);
    } catch (err) {
      if (!autoTrigger) {
        console.error('Unable to calculate recommendations', err);
        const message = err?.response?.data?.detail || 'Unable to calculate recommendations right now.';
        setRecommendationError(message);
        setRecommendationResult(null);
      }
    } finally {
      setRecommendationLoading(false);
    }
  }, [
    editingGroupId,
    calculatorGroupId,
    selectedSeedId,
    selectedFertilizerId,
    draftSoilType,
    draftSoilDrainage,
    draftSoilPh,
    draftZoneSource,
    draftZoneCode,
  ]);

  React.useEffect(() => {
    if (!recommendationResult) return;
    if (!autoRecalcQueued) return;
    if (recommendationLoading) return;
    if (!editingGroupId && !calculatorGroupId) return;
    const timer = setTimeout(() => {
      handleCalculateRecommendations(true);
    }, 400);
    return () => clearTimeout(timer);
  }, [autoRecalcQueued, recommendationResult, recommendationLoading, editingGroupId, calculatorGroupId, handleCalculateRecommendations]);

  React.useEffect(() => {
    if (isEditing || calculatorMode === 'view') return;
    setCalculatorGroupId(null);
    clearRecommendations();
  }, [selectionKey, isEditing, calculatorMode, clearRecommendations]);

  React.useEffect(() => {
    if (calculatorMode !== 'compose') return;
    if (selectedPolygons.length === 0) return;
    setDraftSoilType('');
    setDraftSoilDrainage('');
    setDraftSoilPh('');
    setPhHelperText('');
  }, [selectionKey, calculatorMode, selectedPolygons]);

  React.useEffect(() => {
    if ((calculatorMode !== 'view' && calculatorMode !== 'edit') || !calculatorGroupId) return;
    if (recommendationLoading) return;
    if (!selectedSeedId && !selectedFertilizerId) return;
    if (recommendationResult) return;
    handleCalculateRecommendations(true, calculatorGroupId);
  }, [
    calculatorMode,
    calculatorGroupId,
    selectedSeedId,
    selectedFertilizerId,
    recommendationResult,
    recommendationLoading,
    handleCalculateRecommendations,
  ]);

  const handleStartNewPlan = React.useCallback(() => {
    hydratedPlanRef.current = null;
    resetFlowDraft();
    setSelectedPlanIds(new Set());
    flowSessionRef.current = true;
    navigate(plantingPlanLocation('new', 'ground'), { state: { flow: true } });
  }, [navigate, resetFlowDraft]);

  const handleBackToLibrary = React.useCallback(() => {
    flowSessionRef.current = false;
    hydratedPlanRef.current = 'leaving';
    resetFlowDraft();
    navigate(TOOLKIT_PLANTING_PATH);
  }, [navigate, resetFlowDraft]);

  const handleSavePlanFromGrowth = React.useCallback(async () => {
    if (savingGroup || planSaved) return;
    const place = buildGroundPlaceFromSelection(draftPlace);
    const manualAcres = parseGroundAcres(draftManualAcres);
    const hasMapPlots = selectedPolygons.length > 0;
    if (!hasMapPlots) {
      if (!(manualAcres > 0) || !place) {
        show({
          title: 'Add acres and town to save',
          body: 'Manual plans need acreage and a town (or a map plot). Go back to Ground, then save.',
          variant: 'warning',
        });
        return;
      }
    }
    if (mode === 'club' && !selectedClubId) {
      show({
        title: 'Select a club',
        body: 'Club plans need a club selected before saving.',
        variant: 'warning',
      });
      return;
    }
    let preview = draftPlanPreview;
    if (!preview) {
      try {
        preview = await toolkitService.getPlanPreview(planPreviewRequest({
          answers: draftWizardAnswers,
          place: {
            zone: draftZoneCode,
            series: draftSoilSeries,
            drain: draftSoilDrainage,
            soilTypeId: draftSoilType,
            ph: draftSoilPh,
            first: draftFrostFirstLabel,
            last: draftFrostLastLabel,
          },
          acres: hasMapPlots ? selectedTotal : manualAcres,
          blend: draftBlendKey,
          draftSoilPh,
        }));
      } catch (err) {
        console.error('Plan preview for save failed', err);
        preview = null;
      }
    }
    const blendKey = sanitizeBlendKey(draftBlendKey)
      || sanitizeBlendKey(preview?.blend?.key)
      || blendKeyFromAnswers(draftWizardAnswers, draftBlendKey);
    const recName = preview?.blend?.name || catalogBlendDisplayName(blendKey);
    const seasonKey = draftWizardAnswers?.season === 'spring' ? 'spring' : 'fall';
    const name = stripPlanNameAcres(draftGroupName.trim());
    if (!hasPlanName(name)) {
      setPlanNameNeeded(true);
      goToFlowStep('ground');
      show({
        title: 'Name this plan',
        body: 'Give it a name on Ground — something you’ll recognize at the co-op, like “Back Ridge fields”.',
        variant: 'warning',
      });
      return;
    }
    const note = buildPlantingPlanNote({
      note: draftGroupNote,
      recName,
      seasonKey,
      placeLabel: place?.label || '',
    });
    const isUpdate = Boolean(calculatorGroupId);
    setSavingGroup(true);
    setSaveError(null);
    try {
      const personalIds = mode === 'personal' ? Array.from(selectedIds).map((id) => Number(id)) : [];
      const clubIds = mode === 'club' ? Array.from(selectedIds).map((id) => Number(id)) : [];
      const payload = {
        name,
        note,
        personal_polygon_ids: personalIds,
        club_boundary_ids: clubIds,
        planting_season: seasonKey,
        wizard_answers: {
          ...sanitizeWizardAnswers(draftWizardAnswers),
          ...(blendKey ? { blend: blendKey } : {}),
        },
      };
      if (!hasMapPlots && manualAcres > 0) {
        payload.manual_acres = manualAcres;
      }
      if (place && Number.isFinite(Number(place.latitude)) && Number.isFinite(Number(place.longitude))) {
        payload.place_latitude = Number(place.latitude);
        payload.place_longitude = Number(place.longitude);
        payload.place_label = String(place.label || place.name || '').trim();
      } else if (hasMapPlots) {
        // Plot-only saves still need a representative point for My Plans plant-week.
        const plotPoint = getPlotCentroid(pickPrimaryPlot(selectedPolygons));
        if (plotPoint
          && Number.isFinite(Number(plotPoint.latitude))
          && Number.isFinite(Number(plotPoint.longitude))) {
          payload.place_latitude = Number(plotPoint.latitude);
          payload.place_longitude = Number(plotPoint.longitude);
        }
      }
      Object.assign(payload, buildMetadataPayload({
        zoneSource: draftZoneSource || 'auto',
        zoneCode: draftZoneCode || place?.zone || '',
        soilType: draftSoilType,
        soilDrainage: draftSoilDrainage,
        soilPh: draftSoilPh,
        soilNotes: draftSoilNotes,
        frostSource: draftFrostSource || 'auto',
        frostLastLabel: draftFrostLastLabel,
        frostFirstLabel: draftFrostFirstLabel,
      }));
      if (mode === 'club') payload.club = selectedClubId;
      const saved = isUpdate
        ? await toolkitService.updateAreaGroup(calculatorGroupId, payload)
        : await toolkitService.createAreaGroup(payload);
      const normalized = {
        ...saved,
        ...(Number.isFinite(payload.manual_acres) && payload.manual_acres > 0
          ? { manual_acres: payload.manual_acres }
          : {}),
        personal_polygon_ids: (saved.personal_polygon_ids || []).map((id) => String(id)),
        club_boundary_ids: (saved.club_boundary_ids || []).map((id) => String(id)),
      };
      normalized.total_acres = resolvePlanAcres(normalized);
      if (isUpdate) {
        setSavedGroups((prev) => prev.map((group) => (
          String(group.id) === String(normalized.id) ? normalized : group
        )));
      } else {
        setSavedGroups((prev) => [normalized, ...prev]);
        setCalculatorGroupId(saved.id);
      }
      setPlanSaved(true);
      setDraftGroupNote(note);
      const windowBit = saved.window_start && saved.window_end
        ? `${saved.window_start} – ${saved.window_end}`
        : seasonKey;
      show({
        title: isUpdate ? 'PLAN UPDATED' : 'PLAN SAVED TO MY PLANS',
        body: `${recName} · ${windowBit}`,
        variant: 'success',
      });
    } catch (err) {
      console.error('Unable to save planting plan', err);
      const apiMessage = err?.response?.data;
      const message = typeof apiMessage === 'string'
        ? apiMessage
        : apiMessage?.manual_acres?.[0]
          || apiMessage?.personal_polygon_ids?.[0]
          || apiMessage?.detail
          || 'Unable to save plan. Please try again.';
      setSaveError(typeof message === 'string' ? message : 'Unable to save plan.');
      show({
        title: 'Save failed',
        body: typeof message === 'string' ? message : 'Unable to save plan.',
        variant: 'danger',
      });
    } finally {
      setSavingGroup(false);
    }
  }, [
    savingGroup,
    planSaved,
    selectedPolygons,
    mode,
    selectedClubId,
    draftPlace,
    draftManualAcres,
    draftWizardAnswers,
    draftBlendKey,
    draftPlanPreview,
    selectedTotal,
    draftSoilSeries,
    draftGroupName,
    draftGroupNote,
    draftZoneSource,
    draftZoneCode,
    draftSoilType,
    draftSoilDrainage,
    draftSoilPh,
    draftSoilNotes,
    draftFrostSource,
    draftFrostLastLabel,
    draftFrostFirstLabel,
    selectedIds,
    calculatorGroupId,
    goToFlowStep,
    show,
  ]);

  const handleContinueFlow = React.useCallback(() => {
    if (flowStep === 'growth') {
      if (planSaved) {
        handleBackToLibrary();
        return;
      }
      handleSavePlanFromGrowth();
      return;
    }
    if (flowStep === 'ground' && !isGroundReady(draftManualAcres, draftPlace, selectedIds.size)) {
      show({
        title: 'Tell us about the ground',
        body: 'Enter acres and pick a closest town from search (or pull both from a map plot) before continuing.',
        variant: 'warning',
      });
      return;
    }
    if (flowStep === 'ground' && !hasPlanName(draftGroupName)) {
      setPlanNameNeeded(true);
      show({
        title: 'Name this plan',
        body: 'Give this plan a name before continuing — something you’ll recognize, like “Back Ridge fields”.',
        variant: 'warning',
      });
      return;
    }
    if (flowStep === 'wizard') {
      if (!canAdvanceWizard(draftWizardAnswers, draftWizardIndex)) {
        show({
          title: 'Pick one',
          body: 'Choose an answer before moving on.',
          variant: 'warning',
        });
        return;
      }
      const nextIndex = advanceWizardIndex(draftWizardAnswers, draftWizardIndex);
      if (nextIndex !== draftWizardIndex) {
        setDraftWizardIndex(nextIndex);
        return;
      }
      setRevealStage(0);
      goToFlowStep('reveal');
      return;
    }
    if (flowStep === 'reveal' && revealStage < REVEAL_STAGE_MAX) {
      return;
    }
    const next = nextPlantingFlowStep(flowStep);
    if (!next) return;
    if (flowStep === 'ground') {
      setCalculatorMode((prev) => (prev === 'view' || prev === 'edit' ? prev : 'compose'));
      const place = buildGroundPlaceFromSelection(draftPlace);
      if (place?.zone && !draftZoneCode) {
        setDraftZoneCode(place.zone);
        setDraftZoneSource((prev) => prev || 'auto');
      }
    }
    goToFlowStep(next);
  }, [
    flowStep,
    planSaved,
    handleSavePlanFromGrowth,
    draftManualAcres,
    draftPlace,
    selectedIds.size,
    draftZoneCode,
    draftWizardAnswers,
    draftWizardIndex,
    revealStage,
    draftGroupName,
    goToFlowStep,
    handleBackToLibrary,
    show,
  ]);

  const handleWizardSelectOption = React.useCallback((optionId) => {
    const result = applyWizardAnswer(draftWizardAnswers, draftWizardIndex, optionId);
    setDraftWizardAnswers(result.answers);
    setDraftWizardIndex(result.index);
    setDraftBlendKey('');
    setDraftPlanPreview(null);
  }, [draftWizardAnswers, draftWizardIndex]);

  const handleWizardSelectQuestion = React.useCallback((index) => {
    setDraftWizardIndex(index);
  }, []);

  const handleRevealStageChange = React.useCallback((stage) => {
    setRevealStage(stage);
  }, []);

  const groundPlace = React.useMemo(
    () => buildConfirmedGroundPlace({
      place: buildGroundPlaceFromSelection(draftPlace),
      zoneCode: draftZoneCode,
      soilSeries: draftSoilSeries,
      soilTypeLabel: soilTypePresetLabel(draftSoilType),
      soilTypeId: draftSoilType,
      drainageLabel: draftSoilDrainage,
      drainageRaw: draftSoilDrainageRaw,
      soilPh: draftSoilPh,
      frostLastLabel: draftFrostLastLabel,
      frostFirstLabel: draftFrostFirstLabel,
      soilSurveyCovered: draftSoilSurveyCovered,
    }),
    [
      draftPlace,
      draftZoneCode,
      draftSoilSeries,
      draftSoilType,
      draftSoilDrainage,
      draftSoilDrainageRaw,
      draftSoilPh,
      draftFrostLastLabel,
      draftFrostFirstLabel,
      draftSoilSurveyCovered,
    ]
  );

  const handleStripStepSelect = React.useCallback((stepId) => {
    goToFlowStep(stepId);
  }, [goToFlowStep]);

  const handleTogglePlanSelect = React.useCallback((planId) => {
    setSelectedPlanIds((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) next.delete(planId);
      else next.add(planId);
      return next;
    });
  }, []);

  const handleShareGroup = React.useCallback((group) => {
    setShareGroup(group);
  }, []);

  const handlePlanSharePatched = React.useCallback((next) => {
    if (!next?.id) return;
    setSavedGroups((prev) => prev.map((item) => (item.id === next.id ? { ...item, ...next } : item)));
    setShareGroup(next);
  }, [setSavedGroups]);

  const handlePlanShared = React.useCallback((next, club) => {
    handlePlanSharePatched(next);
    show({
      title: shareSentToast(club?.name),
      variant: 'success',
    });
  }, [handlePlanSharePatched, show]);

  const handlePlanUnshared = React.useCallback((next, club) => {
    handlePlanSharePatched(next);
    show({
      title: shareUnsharedToast(club?.name),
      variant: 'success',
    });
  }, [handlePlanSharePatched, show]);

  const handleMoreGroup = React.useCallback((group) => {
    setMoreMenuGroupId((prev) => (prev === group.id ? null : group.id));
  }, []);

  const handleMarkPlanted = React.useCallback((group) => {
    setMoreMenuGroupId(null);
    setMarkPlantedGroup(group);
  }, []);

  const handleExportPlanFromCard = React.useCallback(async (group) => {
    if (!group || exportPdfLoading) return;
    setExportPdfLoading(true);
    setExportingPlanId(group.id);
    try {
      const profileRes = await api.get('/profiles/me/');
      const sourcePlots = group.club
        ? (clubPolygonsById[group.club] || [])
        : polygons;
      const plots = resolveGroupPlotsForExport(group, sourcePlots);
      const clubName = group.club
        ? (presidentClubs.find((club) => String(club.id) === String(group.club))?.name || '')
        : '';
      const payload = await buildFieldEditionExportPayload({
        group,
        userDisplayName: formatUserDisplayName(profileRes?.data || {}),
        plots,
        clubName,
        zoneLabel: group.usda_zone_code ? String(group.usda_zone_code).toUpperCase() : '',
        soilTypeLabel: resolveOptionLabel(groupOptions.soilTypes, group.soil_type)
          || soilTypePresetLabel(group.soil_type),
        soilDrainageLabel: resolveOptionLabel(groupOptions.soilDrainage, group.soil_drainage)
          || String(group.soil_drainage || '').replace(/_/g, ' '),
        loadPlan: toolkitService.getPlanPreview,
      });
      await downloadPlantingEstimatePdf(payload);
    } catch (err) {
      console.error('PDF export failed', err);
      show({
        title: 'Export failed',
        body: 'Unable to generate the PDF right now. Please try again.',
        variant: 'danger',
      });
    } finally {
      setExportPdfLoading(false);
      setExportingPlanId(null);
    }
  }, [
    exportPdfLoading,
    clubPolygonsById,
    polygons,
    presidentClubs,
    groupOptions,
    show,
  ]);

  const handleExportSelectedPlans = React.useCallback(() => {
    const count = selectedPlanIds.size;
    show({
      title: 'Export list',
      body: count === 1
        ? 'Use EXPORT on the plan card for a single PDF. Combined shopping-list export comes later.'
        : `Selected ${count} plans. Combined shopping-list export comes later. Use EXPORT on a card for one plan.`,
      variant: 'info',
    });
  }, [selectedPlanIds, show]);

  const loadGroupSelection = React.useCallback(async (group) => {
    previousSelectionRef.current = new Set(selectedIdsRef.current);
    if (group.club) {
      setMode('club');
      setSelectedClubId(group.club);
      try {
        await ensureClubPolygons(group.club);
      } catch (_) {}
      setSelectedIds(new Set((group.club_boundary_ids || []).map((id) => String(id))));
    } else {
      setMode('personal');
      setSelectedClubId(null);
      setSelectedIds(new Set((group.personal_polygon_ids || []).map((id) => String(id))));
    }
  }, [ensureClubPolygons, setSelectedIds]);

  const populateDraftFromGroup = React.useCallback((group) => {
    setDraftGroupName(stripPlanNameAcres(group.name));
    setDraftGroupNote(group.note || '');
    const acres = resolvePlanAcres(group);
    setDraftManualAcres(acres > 0 ? formatGroundAcresInput(acres) : '');
    const lat = Number(group.place_latitude);
    const lng = Number(group.place_longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setDraftPlace({
        latitude: lat,
        longitude: lng,
        label: group.place_label || group.place_name || '',
        name: group.place_name || group.place_label || '',
        zone: group.usda_zone_code || '',
      });
      setDraftPlaceQuery(group.place_label || group.place_name || group.town || '');
    } else {
      setDraftPlace(null);
      setDraftPlaceQuery(group.town || group.place_name || '');
    }
    const incomingZoneSource = group.zone_source || (group.usda_zone_code ? 'auto' : '');
    setDraftZoneSource(incomingZoneSource);
    setDraftZoneCode(group.usda_zone_code || '');
    setDraftSoilType(group.soil_type || '');
    setDraftSoilDrainage(group.soil_drainage || '');
    setDraftSoilPh(group.soil_ph !== null && group.soil_ph !== undefined ? String(group.soil_ph) : '');
    setDraftSoilNotes(group.soil_notes || '');
    setDraftSoilSeries('');
    setDraftSoilDrainageRaw('');
    setDraftSoilSurveyCovered(Boolean(group.soil_type || group.soil_drainage || group.soil_ph));
    setDraftFrostLastLabel(group.last_frost_label || '');
    setDraftFrostFirstLabel(group.first_frost_label || '');
    setDraftFrostSource(
      group.frost_source
      || ((group.last_frost_label || group.first_frost_label) ? 'auto' : '')
    );
    const hydratedAnswers = hydrateWizardAnswers(group.wizard_answers, group.planting_season);
    setDraftWizardAnswers(hydratedAnswers);
    setDraftWizardIndex(wizardIndexForHydratedAnswers(hydratedAnswers));
    setDraftBlendKey(sanitizeBlendKey(group.wizard_answers?.blend));
    setDraftPlanPreview(null);
  }, [setDraftWizardAnswers, setDraftWizardIndex, setDraftBlendKey, setDraftPlanPreview]);

  const handleSaveFromCalculator = async () => {
    if (selectedPolygons.length === 0 || savingGroup) return;
    if (mode === 'club' && !selectedClubId) {
      setSaveError('Select a club before saving.');
      return;
    }
    const trimmed = draftGroupName.trim();
    if (!trimmed) {
      setSaveError('Name is required.');
      return;
    }
    if (draftZoneSource === 'manual' && !draftZoneCode) {
      setSaveError('Select a USDA zone when overriding manually.');
      return;
    }
    if (!selectedSeedId && !selectedFertilizerId) {
      setSaveError('Select at least a seed or fertilizer preset.');
      return;
    }
    setSaveError(null);
    setSavingGroup(true);
    clearRecommendations();
    try {
      const personalIds = mode === 'personal' ? Array.from(selectedIds).map((id) => Number(id)) : [];
      const clubIds = mode === 'club' ? Array.from(selectedIds).map((id) => Number(id)) : [];
      const payload = {
        name: trimmed,
        note: draftGroupNote,
        personal_polygon_ids: personalIds,
        club_boundary_ids: clubIds,
      };
      Object.assign(payload, buildMetadataPayload({
        zoneSource: draftZoneSource,
        zoneCode: draftZoneCode,
        soilType: draftSoilType,
        soilDrainage: draftSoilDrainage,
        soilPh: draftSoilPh,
        soilNotes: draftSoilNotes,
        frostSource: draftFrostSource,
        frostLastLabel: draftFrostLastLabel,
        frostFirstLabel: draftFrostFirstLabel,
      }));
      if (mode === 'club') payload.club = selectedClubId;
      const saved = await toolkitService.createAreaGroup(payload);
      const normalized = {
        ...saved,
        personal_polygon_ids: (saved.personal_polygon_ids || []).map((id) => String(id)),
        club_boundary_ids: (saved.club_boundary_ids || []).map((id) => String(id)),
      };
      setSavedGroups((prev) => [normalized, ...prev]);
      finishNewGroupSave(saved.id);
      show({
        title: 'Estimate saved',
        body: `${saved.polygon_count} polygons saved with your seed estimate.`,
        variant: 'success',
      });
      await handleCalculateRecommendations(false, saved.id);
    } catch (err) {
      console.error('Unable to save area group', err);
      const apiMessage = err?.response?.data;
      const message = typeof apiMessage === 'string'
        ? apiMessage
        : apiMessage?.personal_polygon_ids?.[0]
          || apiMessage?.club_boundary_ids?.[0]
          || apiMessage?.detail
          || 'Unable to save estimate. Please try again.';
      setSaveError(message);
      if (!err?.response) {
        try {
          await loadGroups();
        } catch (_) {
          /* ignore refetch errors */
        }
      }
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!groupId || deletingGroupId === groupId) return;
    setDeletingGroupId(groupId);
    try {
      await toolkitService.deleteAreaGroup(groupId);
      setSavedGroups((prev) => prev.filter((group) => group.id !== groupId));
      setPlantingEvents((prev) => prev.map((row) => (
        row.area_group === groupId ? { ...row, area_group: null } : row
      )));
      show({ title: 'Area group removed', variant: 'info' });
    } catch (err) {
      console.error('Unable to delete area group', err);
      show({ title: 'Delete failed', body: 'Unable to delete this group right now.', variant: 'danger' });
    } finally {
      setDeletingGroupId(null);
    }
  };

  const startEditingGroup = React.useCallback((group) => {
    if (!group) return;
    const run = async () => {
      setEditingError(null);
      setSaveError(null);
      clearRecommendations();
      setCalculatorMode('edit');
      setEditingGroupId(group.id);
      setCalculatorGroupId(group.id);
      populateDraftFromGroup(group);
      await loadGroupSelection(group);
      hydratedPlanRef.current = String(group.id);
      flowSessionRef.current = true;
      navigate(plantingPlanLocation(group.id, 'ground'), { state: { flow: true } });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    run();
  }, [clearRecommendations, loadGroupSelection, populateDraftFromGroup, navigate]);

  const startViewingGroup = React.useCallback((group) => {
    if (!group) return;
    const run = async () => {
      setEditingError(null);
      setSaveError(null);
      clearRecommendations();
      setCalculatorMode('view');
      setEditingGroupId(null);
      setCalculatorGroupId(group.id);
      populateDraftFromGroup(group);
      await loadGroupSelection(group);
      hydratedPlanRef.current = String(group.id);
      flowSessionRef.current = true;
      navigate(plantingPlanLocation(group.id, 'plan'), { state: { flow: true } });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    run();
  }, [clearRecommendations, loadGroupSelection, populateDraftFromGroup, navigate]);

  const handlePlantingLogged = React.useCallback((created) => {
    if (created?.id) {
      setPlantingEvents((prev) => [created, ...prev.filter((row) => row.id !== created.id)]);
    } else {
      loadPlantingEvents();
    }
    show({
      title: 'Planting logged',
      body: 'The plan stays editable. You can log another planting anytime.',
      variant: 'success',
    });
  }, [loadPlantingEvents, show]);

  const handleOpenPlantingEvent = React.useCallback((event) => {
    const plan = findLivePlanForEvent(event, savedGroups);
    if (plan) {
      startViewingGroup(plan);
      return;
    }
    setMissingPlanEvent(event);
  }, [savedGroups, startViewingGroup]);

  const handleAskDeletePlantingEvent = React.useCallback((event) => {
    setDeleteLogError('');
    setPendingDeleteEvent(event);
  }, []);

  const handleConfirmDeletePlantingEvent = React.useCallback(async () => {
    const eventId = pendingDeleteEvent?.id;
    if (!eventId || deletingLogEvent) return;
    setDeletingLogEvent(true);
    setDeleteLogError('');
    try {
      await toolkitService.deletePlantingEvent(eventId);
      setPlantingEvents((prev) => prev.filter((row) => row.id !== eventId));
      setPendingDeleteEvent(null);
      show({
        title: 'Planting removed',
        body: 'That dated record is off the log. The plan is still in My Plans.',
        variant: 'info',
      });
    } catch (err) {
      setDeleteLogError(err?.response?.data?.detail || err?.message || 'Could not remove this planting.');
    } finally {
      setDeletingLogEvent(false);
    }
  }, [pendingDeleteEvent, deletingLogEvent, show]);

  const handleEditFromView = React.useCallback(() => {
    if (!calculatorGroupId) return;
    setCalculatorMode('edit');
    setEditingGroupId(calculatorGroupId);
    setEditingError(null);
    setSaveError(null);
  }, [calculatorGroupId]);

  const handleViewPlanEdit = React.useCallback(() => {
    const group = savedGroups.find((item) => item.id === calculatorGroupId);
    if (group) {
      startEditingGroup(group);
      return;
    }
    handleEditFromView();
    goToFlowStep('ground');
  }, [savedGroups, calculatorGroupId, startEditingGroup, handleEditFromView, goToFlowStep]);

  const handleStickyFlowCta = React.useCallback(() => {
    if (calculatorMode === 'view') {
      handleViewPlanEdit();
      return;
    }
    handleContinueFlow();
  }, [calculatorMode, handleViewPlanEdit, handleContinueFlow]);

  const handleCancelEditGroup = () => {
    clearRecommendations();
    if (calculatorGroupId) {
      setCalculatorMode('view');
      setEditingGroupId(null);
      setEditingError(null);
      if (previousSelectionRef.current) {
        setSelectedIds(new Set(previousSelectionRef.current));
      }
      previousSelectionRef.current = null;
      handleCalculateRecommendations(true, calculatorGroupId);
      return;
    }
    setCalculatorMode('compose');
    setEditingGroupId(null);
    setDraftGroupName('');
    setDraftGroupNote('');
    setDraftZoneSource('');
    setDraftZoneCode('');
    setDraftSoilType('');
    setDraftSoilDrainage('');
    setDraftSoilPh('');
    setDraftSoilNotes('');
    setEditingError(null);
    if (previousSelectionRef.current) {
      setSelectedIds(new Set(previousSelectionRef.current));
    }
    previousSelectionRef.current = null;
  };

  const handleDetectZone = async () => {
    if (!editingGroupId || detectZoneLoading || editingManualOverride) return;
    setDetectZoneError(null);
    setDetectZoneLoading(true);
    try {
      const updated = await toolkitService.detectAreaGroupZone(editingGroupId);
      setDraftZoneCode(updated.usda_zone_code || '');
      setDraftZoneSource(updated.zone_source || 'auto');
      setSavedGroups((prev) => prev.map((g) => (g.id === updated.id ? { ...g, ...updated } : g)));
      show({ title: 'Zone detected', body: `USDA zone ${(updated.usda_zone_code || '').toUpperCase()}`, variant: 'success' });
    } catch (err) {
      console.error('USDA zone detect failed', err);
      const detail = err?.response?.data?.detail || 'Could not detect USDA zone for this area.';
      setDetectZoneError(typeof detail === 'string' ? detail : 'Could not detect USDA zone.');
    } finally {
      setDetectZoneLoading(false);
    }
  };

  const handleSaveEditGroup = async () => {
    if (!editingGroupId || editingSaveLoading) return;
    const trimmed = draftGroupName.trim();
    if (!trimmed) {
      setEditingError('Name is required.');
      return;
    }
    if (selectedPolygons.length === 0) {
      setEditingError('Select at least one polygon.');
      return;
    }
    if (draftZoneSource === 'manual' && !draftZoneCode) {
      setEditingError('Select a USDA zone when overriding manually.');
      return;
    }
    setEditingError(null);
    setEditingSaveLoading(true);
    try {
      if (mode === 'club' && !selectedClubId) {
        setEditingError('Select a club before saving.');
        setEditingSaveLoading(false);
        return;
      }
      const personalIds = mode === 'personal' ? Array.from(selectedIds).map((id) => Number(id)) : [];
      const clubIds = mode === 'club' ? Array.from(selectedIds).map((id) => Number(id)) : [];
      const payload = {
        name: trimmed,
        note: draftGroupNote,
        personal_polygon_ids: personalIds,
        club_boundary_ids: clubIds
      };
      const metadataPayload = buildMetadataPayload({
        zoneSource: draftZoneSource,
        zoneCode: draftZoneCode,
        soilType: draftSoilType,
        soilDrainage: draftSoilDrainage,
        soilPh: draftSoilPh,
        soilNotes: draftSoilNotes,
        frostSource: draftFrostSource,
        frostLastLabel: draftFrostLastLabel,
        frostFirstLabel: draftFrostFirstLabel,
      });
      Object.assign(payload, metadataPayload);
      if (mode === 'club') payload.club = selectedClubId;
      const updated = await toolkitService.updateAreaGroup(editingGroupId, payload);
      const normalized = {
        ...updated,
        personal_polygon_ids: (updated.personal_polygon_ids || []).map((id) => String(id)),
        club_boundary_ids: (updated.club_boundary_ids || []).map((id) => String(id)),
      };
      normalized.total_acres = resolvePlanAcres(normalized);
      setSavedGroups((prev) => prev.map((group) => (
        String(group.id) === String(normalized.id) ? normalized : group
      )));
      show({ title: 'Area group updated', body: `${normalized.polygon_count} polygons saved.`, variant: 'success' });
      const updatedGroupId = editingGroupId;
      setCalculatorMode('view');
      setEditingGroupId(null);
      setEditingError(null);
      if (selectedSeedId || selectedFertilizerId) {
        await handleCalculateRecommendations(true, updatedGroupId);
      }
    } catch (err) {
      console.error('Unable to update area group', err);
      const apiMessage = err?.response?.data;
      const message = typeof apiMessage === 'string'
        ? apiMessage
        : apiMessage?.personal_polygon_ids?.[0]
          || apiMessage?.detail
          || 'Unable to update this group right now.';
      setEditingError(message);
      if (!err?.response) {
        try {
          await loadGroups();
        } catch (_) {
          /* ignore refetch errors */
        }
      }
    } finally {
      setEditingSaveLoading(false);
    }
  };

  const zoneOptionsList = React.useMemo(() => groupOptions.zones || [], [groupOptions.zones]);
  const soilTypeOptions = React.useMemo(() => groupOptions.soilTypes || [], [groupOptions.soilTypes]);
  const soilDrainageOptions = React.useMemo(() => groupOptions.soilDrainage || [], [groupOptions.soilDrainage]);

  const handleGroundSoilTypeOverride = React.useCallback((typeId) => {
    setDraftSoilType(typeId);
    setDraftSoilTypeSource('manual');
    const label = resolveOptionLabel(soilTypeOptions, typeId) || soilTypePresetLabel(typeId);
    if (label) setDraftSoilSeries(label);
  }, [setDraftSoilType, setDraftSoilTypeSource, setDraftSoilSeries, soilTypeOptions]);

  const handleGroundSoilDrainageOverride = React.useCallback((drainageId) => {
    setDraftSoilDrainage(drainageId);
    setDraftSoilDrainageSource('manual');
    const label = resolveOptionLabel(soilDrainageOptions, drainageId);
    if (label) setDraftSoilDrainageRaw(label);
  }, [
    setDraftSoilDrainage,
    setDraftSoilDrainageSource,
    setDraftSoilDrainageRaw,
    soilDrainageOptions,
  ]);
  const soilNotePlaceholder = groupOptions.soilNotePlaceholder || 'Describe soil or history';
  const editingManualOverride = draftZoneSource === 'manual';
  const editingZoneLabel = draftZoneCode
    ? draftZoneCode.toUpperCase()
    : (draftZoneSource === 'manual' ? 'Not set' : 'Not set — use manual override');

  const canExportPdf = React.useMemo(
    () => canShowPlantingExport({
      calculatorMode,
      selectedSeedId,
      selectedFertilizerId,
      selectedPolygonCount: selectedPolygons.length,
    }),
    [calculatorMode, selectedSeedId, selectedFertilizerId, selectedPolygons.length]
  );

  const handleExportPdf = React.useCallback(async () => {
    if (!canExportPdf || exportPdfLoading) return;
    setExportPdfLoading(true);
    try {
      const profileRes = await api.get('/profiles/me/');
      const zoneOption = zoneOptionsList.find(
        (zone) => String(zone.value).toLowerCase() === String(draftZoneCode).toLowerCase()
      );
      const zoneLabel = zoneOption ? formatZoneOptionLabel(zoneOption) : editingZoneLabel;
      const normalizedZoneLabel = ['Not set', 'Not set — use manual override'].includes(zoneLabel)
        ? ''
        : zoneLabel;

      const payload = buildPlantingExportPayload({
        userDisplayName: formatUserDisplayName(profileRes?.data || {}),
        groupName: draftGroupName,
        groupNote: draftGroupNote,
        clubName: selectedClubName,
        isClubGroup: mode === 'club' && Boolean(selectedClubId),
        zoneLabel: normalizedZoneLabel,
        soilTypeLabel: resolveOptionLabel(soilTypeOptions, draftSoilType),
        soilDrainageLabel: resolveOptionLabel(soilDrainageOptions, draftSoilDrainage),
        soilPh: draftSoilPh,
        soilNotes: draftSoilNotes,
        plots: selectedPolygons,
        totalAcres: selectedTotal,
        selectedSeedId,
        selectedFertilizerId,
        seedOptions,
        fertilizerOptions,
        recommendationResult,
      });

      await downloadPlantingEstimatePdf(payload);
    } catch (err) {
      console.error('PDF export failed', err);
      show({
        title: 'Export failed',
        body: 'Unable to generate the PDF right now. Please try again.',
        variant: 'danger',
      });
    } finally {
      setExportPdfLoading(false);
    }
  }, [
    canExportPdf,
    exportPdfLoading,
    draftGroupName,
    draftGroupNote,
    selectedClubName,
    mode,
    selectedClubId,
    zoneOptionsList,
    draftZoneCode,
    editingZoneLabel,
    soilTypeOptions,
    draftSoilType,
    soilDrainageOptions,
    draftSoilDrainage,
    draftSoilPh,
    draftSoilNotes,
    selectedPolygons,
    selectedTotal,
    selectedSeedId,
    selectedFertilizerId,
    seedOptions,
    fertilizerOptions,
    recommendationResult,
    show,
  ]);

  const extractGeometryForLookup = React.useCallback((poly) => {
    if (!poly) return null;
    return poly.geojson || poly.raw?.geojson || poly.raw?.geometry || null;
  }, []);

  const applyGroundZoneResult = React.useCallback((zoneResult) => {
    const sources = groundFieldSourcesRef.current;
    if (zoneResult?.__error) {
      setDetectZoneError(zoneResult.detail);
      if (shouldApplyAutofill(sources.zone)) {
        setDraftZoneCode('');
        setDraftZoneSource('');
      }
      if (shouldApplyAutofill(sources.frost)) {
        setDraftFrostLastLabel('');
        setDraftFrostFirstLabel('');
      }
      return;
    }
    const code = normalizeZoneCode(zoneResult?.usda_zone_code);
    if (shouldApplyAutofill(sources.zone)) {
      setDraftZoneCode(code);
      setDraftZoneSource(code ? (zoneResult?.zone_source || 'auto') : '');
    }
    if (shouldApplyAutofill(sources.frost)) {
      const frost = frostLabelsFromZoneResult(zoneResult?.frost);
      setDraftFrostLastLabel(frost.lastLabel);
      setDraftFrostFirstLabel(frost.firstLabel);
      if (frost.lastLabel || frost.firstLabel) {
        setDraftFrostSource('auto');
      }
    }
    if (!code && zoneResult?.detail && shouldApplyAutofill(sources.zone)) {
      setDetectZoneError(zoneResult.detail);
    } else {
      setDetectZoneError(null);
    }
  }, [
    setDetectZoneError,
    setDraftZoneCode,
    setDraftZoneSource,
    setDraftFrostLastLabel,
    setDraftFrostFirstLabel,
    setDraftFrostSource,
  ]);

  const applyGroundSoilSurvey = React.useCallback((survey) => {
    if (!survey) return;
    const sources = groundFieldSourcesRef.current;
    const typeId = String(survey.textureId || '').trim();
    const seriesLabel = String(survey.series || survey.textureLabel || '').trim()
      || soilTypePresetLabel(typeId);
    if (shouldApplyAutofill(sources.soilType)) {
      setDraftSoilSeries(seriesLabel);
      if (typeId) {
        setDraftSoilType(typeId);
        setDraftSoilTypeSource('auto');
      }
    }
    if (shouldApplyAutofill(sources.soilDrainage)) {
      setDraftSoilDrainageRaw(survey.drainage || '');
      if (survey.drainageId) {
        setDraftSoilDrainage(survey.drainageId);
        setDraftSoilDrainageSource('auto');
      }
    }
    setDraftSoilSurveyCovered(true);
    if (shouldApplyAutofill(sources.soilPh) && survey.ph != null) {
      setDraftSoilPh(String(survey.ph));
      setDraftSoilPhSource('auto');
      setPhHelperText('Source: soil lookup (USDA horizon pH). Local soil tests recommended.');
    }
  }, [
    setDraftSoilSeries,
    setDraftSoilDrainageRaw,
    setDraftSoilSurveyCovered,
    setDraftSoilType,
    setDraftSoilTypeSource,
    setDraftSoilDrainage,
    setDraftSoilDrainageSource,
    setDraftSoilPh,
    setDraftSoilPhSource,
    setPhHelperText,
  ]);

  // Ground WHAT WE READ: plots win when selected; otherwise geocoded place.
  React.useEffect(() => {
    if (!inFlow || flowStep !== 'ground') return undefined;
    if (calculatorMode === 'view') return undefined;

    const seq = groundZoneLookupSeqRef.current + 1;
    groundZoneLookupSeqRef.current = seq;
    let cancelled = false;

    const run = async () => {
      if (selectedPolygons.length > 0) {
        const primary = pickPrimaryPlot(selectedPolygons);
        const centroid = getPlotCentroid(primary);
        const geom = extractGeometryForLookup(primary);
        // Keep previous soil visible while re-looking up — clearing caused empty/"null" flashes.
        setDetectZoneError(null);
        setDetectZoneLoading(true);
        try {
          const [zoneResult, soilResult] = await Promise.all([
            centroid
              ? toolkitService.lookupUsdaZone({
                latitude: centroid.latitude,
                longitude: centroid.longitude,
              }).catch((err) => {
                console.error('USDA zone lookup failed', err);
                return { __error: true, detail: 'Unable to look up USDA zone right now.' };
              })
              : Promise.resolve(null),
            geom
              ? toolkitService.lookupSoil({
                state: SOIL_LOOKUP_STATE,
                geometry: geom,
              }).catch((err) => {
                console.error('Soil lookup failed', err);
                return null;
              })
              : Promise.resolve(null),
          ]);
          if (cancelled || seq !== groundZoneLookupSeqRef.current) return;
          if (zoneResult) applyGroundZoneResult(zoneResult);
          const survey = soilSurveyFromLookup(soilResult);
          if (survey) {
            applyGroundSoilSurvey(survey);
          } else {
            clearUncoveredSoilDraft();
          }
        } finally {
          if (seq === groundZoneLookupSeqRef.current) setDetectZoneLoading(false);
        }
        return;
      }

      if (hasGroundPlaceCoords(draftPlace)) {
        const lat = Number(draftPlace.latitude);
        const lng = Number(draftPlace.longitude);
        const soilState = resolveSoilLookupState(draftPlace);
        setDetectZoneError(null);
        setDetectZoneLoading(true);
        try {
          const [zoneResult, soilResult] = await Promise.all([
            toolkitService.lookupUsdaZone({ latitude: lat, longitude: lng }).catch((err) => {
              console.error('USDA zone lookup failed', err);
              return { __error: true, detail: 'Unable to look up USDA zone right now.' };
            }),
            soilState
              ? toolkitService.lookupSoil({ latitude: lat, longitude: lng, state: soilState })
                .catch((err) => {
                  console.error('Soil lookup failed', err);
                  return null;
                })
              : Promise.resolve(null),
          ]);
          if (cancelled || seq !== groundZoneLookupSeqRef.current) return;
          applyGroundZoneResult(zoneResult);
          const survey = soilSurveyFromLookup(soilResult);
          if (survey) {
            applyGroundSoilSurvey(survey);
          } else {
            clearUncoveredSoilDraft();
          }
        } finally {
          if (seq === groundZoneLookupSeqRef.current) setDetectZoneLoading(false);
        }
        return;
      }

      clearAutoGroundZone();
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    inFlow,
    flowStep,
    calculatorMode,
    selectedPolygons,
    draftPlace,
    groundSurveyResetToken,
    clearAutoGroundZone,
    clearUncoveredSoilDraft,
    extractGeometryForLookup,
    applyGroundZoneResult,
    applyGroundSoilSurvey,
    setDetectZoneLoading,
    setDetectZoneError,
  ]);

  const applySoilAutofill = React.useCallback(
    (winner) => {
      if (!winner) return;
      const sources = groundFieldSourcesRef.current;
      const { drainageId, textureId, drainageSource, textureSource, drainageRaw, textureRaw, phValue, phSource } = winner;
      const typeAvailable = textureId && (soilTypeOptions || []).some((opt) => opt.id === textureId);
      const drainageAvailable = drainageId && (soilDrainageOptions || []).some((opt) => opt.id === drainageId);
      const phAvailable = phValue !== null && phValue !== undefined;
      if (!draftSoilType && typeAvailable && shouldApplyAutofill(sources.soilType)) {
        setDraftSoilType(textureId);
      }
      if (!draftSoilDrainage && drainageAvailable && shouldApplyAutofill(sources.soilDrainage)) {
        setDraftSoilDrainage(drainageId);
      }
      if (!draftSoilPh && phAvailable && shouldApplyAutofill(sources.soilPh)) {
        setDraftSoilPh(String(phValue));
      }
      if (phAvailable && shouldApplyAutofill(sources.soilPh)) {
        setPhHelperText('Source: soil lookup (USDA horizon pH). Local soil tests recommended.');
      }
      winner.drainageSource = drainageAvailable ? drainageSource : null;
      winner.drainageRaw = drainageAvailable ? drainageRaw : null;
      winner.textureSource = typeAvailable ? textureSource : null;
      winner.textureRaw = typeAvailable ? textureRaw : null;
      winner.phSource = phAvailable ? phSource : null;
    },
    [
      draftSoilDrainage,
      draftSoilPh,
      draftSoilType,
      soilDrainageOptions,
      soilTypeOptions,
    ]
  );

  const chooseWeighted = React.useCallback((items) => {
    const total = items.reduce((acc, item) => acc + item.weight, 0);
    if (total === 0) return { winner: null, mixed: false };
    const tally = items.reduce((map, item) => {
      if (!item.id) return map;
      map[item.id] = (map[item.id] || 0) + item.weight;
      return map;
    }, {});
    const entries = Object.entries(tally);
    if (entries.length === 0) return { winner: null, mixed: false };
    const maxWeight = Math.max(...entries.map(([, w]) => w));
    const top = entries.filter(([, w]) => w === maxWeight);
    if (top.length !== 1) {
      return { winner: null, mixed: true };
    }
    return { winner: top[0][0], mixed: false };
  }, []);

  const aggregateSoilLookups = React.useCallback(
    async (polys) => {
      if (!polys || polys.length === 0) return { winner: null, mixed: false };
      // Run sequentially — parallel soil lookups can OOM the 512MB production worker (pyogrio + FGB download).
      const lookups = [];
      for (const poly of polys) {
        const geom = extractGeometryForLookup(poly);
        if (!geom) {
          lookups.push(null);
          continue;
        }
        try {
          const resp = await api.post('/soil/lookup/', { state: SOIL_LOOKUP_STATE, geometry: geom });
          const attrs = resp?.data?.attributes;
          const area = typeof poly.acreage === 'number' ? poly.acreage : typeof poly.raw?.acreage === 'number' ? poly.raw.acreage : 1;
          lookups.push({
            hasDrainageClass: Boolean(attrs?.drainage_class && attrs.drainage_class !== 'Unknown'),
            drainageId: attrs?.drainage_class_normalized || null,
            drainageSource: attrs?.drainage_class_source || null,
            drainageRaw: attrs?.drainage_class || null,
            textureId: attrs?.texture_class_normalized || null,
            textureSource: attrs?.texture_class_source || null,
            textureRaw: attrs?.texture_class || null,
            phValue: attrs && attrs.ph !== undefined && attrs.ph !== null ? attrs.ph : null,
            phSource: attrs && attrs.ph !== undefined && attrs.ph !== null ? 'soil_lookup' : null,
            weight: Math.max(area || 1, 1)
          });
        } catch (_) {
          lookups.push(null);
        }
      }
      const cleaned = lookups.filter(Boolean);
      const drainagePick = chooseWeighted(cleaned.map((r) => ({ id: r.drainageId, weight: r.weight })));
      const texturePick = chooseWeighted(cleaned.map((r) => ({ id: r.textureId, weight: r.weight })));
      const phCandidate = cleaned.filter((r) => r.phValue !== null && r.phValue !== undefined);
      const phWinner = phCandidate.length
        ? phCandidate.reduce((best, item) => {
            const bestWeight = best ? best.weight : -1;
            return item.weight > bestWeight ? item : best;
          }, null)
        : null;
      const winner = {
        drainageId: drainagePick.winner,
        drainageSource: drainagePick.winner
          ? (cleaned.find((r) => r.drainageId === drainagePick.winner) || {}).drainageSource
          : null,
        drainageRaw: drainagePick.winner
          ? (cleaned.find((r) => r.drainageId === drainagePick.winner) || {}).drainageRaw
          : null,
        textureId: texturePick.winner,
        textureSource: texturePick.winner
          ? (cleaned.find((r) => r.textureId === texturePick.winner) || {}).textureSource
          : null,
        textureRaw: texturePick.winner
          ? (cleaned.find((r) => r.textureId === texturePick.winner) || {}).textureRaw
          : null,
        phValue: phWinner?.phValue ?? null,
        phSource: phWinner?.phSource ?? null
      };
      return {
        winner,
        mixedDrainage: drainagePick.mixed,
        hadAnyDrainageClass: cleaned.some((r) => r.hasDrainageClass)
      };
    },
    [chooseWeighted, extractGeometryForLookup]
  );

  React.useEffect(() => {
    if (inFlow) return;
    if (optionsLoading) return;
    if (selectedPolygons.length === 0) return;
    if (calculatorMode === 'view') return;
    const needsDraft = !draftSoilType || !draftSoilDrainage;
    if (!needsDraft) return;

    let cancelled = false;
    const run = async () => {
      const { winner, mixedDrainage, hadAnyDrainageClass } = await aggregateSoilLookups(selectedPolygons);
      if (cancelled) return;
      if (winner && (winner.drainageId || winner.textureId)) {
        applySoilAutofill(winner);
        if (winner.drainageId) {
          const drainageLabel = (soilDrainageOptions.find((o) => o.id === winner.drainageId) || {}).label || winner.drainageId;
          const drainageSourceHint =
            winner.drainageSource === 'drainage_class'
              ? `Source: drainage class "${winner.drainageRaw || ''}"`
              : winner.drainageSource === 'hydrologic_group'
                ? `Source: hydrologic group (no drainage class provided)`
                : null;
          const body = drainageSourceHint
            ? `${drainageSourceHint}. Set to ${drainageLabel}. You can override anytime.`
            : `Set drainage to ${drainageLabel} based on largest selected acreage. You can override anytime.`;
          const already = lastAutofillToastRef.current;
          if (!(already && already.type === 'drainage' && already.value === winner.drainageId)) {
            try {
              show({
                title: 'Soil autofill',
                body,
                variant: 'info'
              });
            } catch (_) {}
            lastAutofillToastRef.current = { type: 'drainage', value: winner.drainageId };
          }
        }
      } else if (mixedDrainage) {
        const already = lastAutofillToastRef.current;
        if (!(already && already.type === 'mixed')) {
          try {
            show({
              title: 'Mixed drainage detected',
              body: 'Selected polygons have different drainage. Please pick a drainage value.',
              variant: 'warning'
            });
          } catch (_) {}
          lastAutofillToastRef.current = { type: 'mixed', value: null };
        }
      } else if (!hadAnyDrainageClass) {
        const already = lastAutofillToastRef.current;
        if (!(already && already.type === 'nodrainage')) {
          try {
            show({
              title: 'No drainage class provided',
              body: 'Selected polygons lack drainage class data. Please choose a drainage value.',
              variant: 'warning'
            });
          } catch (_) {}
          lastAutofillToastRef.current = { type: 'nodrainage', value: null };
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    aggregateSoilLookups,
    applySoilAutofill,
    calculatorMode,
    draftSoilDrainage,
    draftSoilType,
    inFlow,
    optionsLoading,
    selectedPolygons,
    show,
    soilDrainageOptions,
  ]);

  React.useEffect(() => {
    // Reset toast dedupe when selection changes
    lastAutofillToastRef.current = null;
  }, [selectionKey]);

  const handleViewOnMap = (group) => {
    if (!group) return;
    const kind = group.club ? 'club' : 'personal';
    setHighlightGroup({
      groupId: group.id,
      name: group.name,
      kind,
      personalPolygonIds: (group.personal_polygon_ids || []).map((id) => String(id)),
      clubBoundaryIds: (group.club_boundary_ids || []).map((id) => String(id)),
      clubId: group.club || null
    });
    const targetPath = group.club ? `/clubs/${group.club}/map` : '/maps';
    try {
      show({ title: 'Opening on map', body: `Highlighting "${group.name}"`, variant: 'info' });
    } catch (_) {}
    navigate(targetPath);
  };

  // Deep-link / refresh guard: new plan needs an active flow session; saved plan must exist.
  React.useEffect(() => {
    if (!inFlow) return undefined;
    if (isNewPlan) {
      const bootstrapped = flowSessionRef.current
        || selectedIds.size > 0
        || Boolean(calculatorGroupId)
        || Boolean(draftGroupName);
      if (!bootstrapped) {
        navigate(TOOLKIT_PLANTING_PATH, { replace: true });
      }
      return undefined;
    }
    if (groupsLoading) return undefined;
    const found = savedGroups.find((group) => String(group.id) === String(flowPlanKey));
    if (!found) {
      navigate(TOOLKIT_PLANTING_PATH, { replace: true });
    } else {
      flowSessionRef.current = true;
    }
    return undefined;
  }, [
    inFlow,
    isNewPlan,
    selectedIds,
    calculatorGroupId,
    draftGroupName,
    groupsLoading,
    savedGroups,
    flowPlanKey,
    navigate,
  ]);

  // Hydrate saved plan when opening /plan/:id
  React.useEffect(() => {
    if (!inFlow || isNewPlan || groupsLoading) return undefined;
    const found = savedGroups.find((group) => String(group.id) === String(flowPlanKey));
    if (!found) return undefined;
    if (hydratedPlanRef.current === String(found.id) || hydratedPlanRef.current === 'leaving') {
      return undefined;
    }
    hydratedPlanRef.current = String(found.id);
    const run = async () => {
      setCalculatorGroupId(found.id);
      setCalculatorMode((prev) => (prev === 'compose' ? 'view' : prev));
      populateDraftFromGroup(found);
      await loadGroupSelection(found);
    };
    run();
    return undefined;
  }, [
    inFlow,
    isNewPlan,
    groupsLoading,
    savedGroups,
    flowPlanKey,
    populateDraftFromGroup,
    loadGroupSelection,
  ]);

  // Default missing ?step= on flow routes
  React.useEffect(() => {
    if (!inFlow) return;
    if (searchParams.get('step')) return;
    setSearchParams({ step: flowStep }, { replace: true, state: location.state });
  }, [inFlow, searchParams, flowStep, setSearchParams, location.state]);

  const moreMenuGroup = React.useMemo(
    () => savedGroups.find((group) => group.id === moreMenuGroupId) || null,
    [savedGroups, moreMenuGroupId]
  );

  const showPlotPicker = inFlow && flowStep === 'ground';
  const showPlanCalculator = inFlow && flowStep === 'plan';
  const showPrototypePlan = showPlanCalculator && usesFieldEditionPlanStep({
    calculatorMode,
    plantingSeason: draftWizardAnswers?.season,
    selectedPolygonCount: selectedPolygons.length,
  });
  const showLegacyPlanCalculator = showPlanCalculator && !showPrototypePlan;
  const blendRec = React.useMemo(() => {
    const key = sanitizeBlendKey(draftPlanPreview?.blend?.key)
      || blendKeyFromAnswers(draftWizardAnswers, draftBlendKey);
    return {
      key,
      name: draftPlanPreview?.blend?.name || catalogBlendDisplayName(key),
      parts: (draftPlanPreview?.blend?.parts || []).map((part) => [part.name, part.lbs_per_acre]),
    };
  }, [draftWizardAnswers, draftBlendKey, draftPlanPreview]);
  const timingSched = React.useMemo(
    () => getPlantingSchedule(draftWizardAnswers),
    [draftWizardAnswers]
  );
  const timingPoint = React.useMemo(
    () => resolveGroundForecastPoint({ selectedPolygons, draftPlace }),
    [selectedPolygons, draftPlace]
  );
  const isViewingPlan = calculatorMode === 'view';
  const flowKicker = plantingFlowKicker(flowStep, {
    acresLabel: selectedTotal > 0 ? `${selectedTotal.toFixed(1)} ACRES` : '',
    zoneLabel: draftZoneCode ? `ZONE ${String(draftZoneCode).toUpperCase()}` : '',
    wizardQuestion: draftWizardIndex + 1,
    planName: blendRec?.name || '',
    windowLabel: timingSched?.windowLabel || '',
    plantShort: typicalPlantLabel(draftWizardAnswers, groundPlace, draftBlendKey),
    viewing: isViewingPlan,
  });
  const planCropName = blendRec?.parts?.[0]?.[0] || 'oats';
  const growthCtaLabel = growthContinueLabel(planSaved, {
    updating: Boolean(calculatorGroupId),
  });
  const groundContinueDisabled = !isGroundStepReady(
    draftManualAcres,
    draftPlace,
    selectedIds.size,
    draftGroupName,
  );
  const wizardCtaLabel = wizardContinueLabel(draftWizardAnswers, draftWizardIndex);
  const wizardContinueDisabled = !canAdvanceWizard(draftWizardAnswers, draftWizardIndex);
  const revealCtaLabel = revealContinueLabel(revealStage);
  const revealContinueDisabled = revealStage < REVEAL_STAGE_MAX;
  const groundSoilTypeLabel = resolveOptionLabel(soilTypeOptions, draftSoilType)
    || soilTypePresetLabel(draftSoilType);
  const groundSoilDrainageLabel = resolveOptionLabel(soilDrainageOptions, draftSoilDrainage);

  const handlePlanOrderClick = React.useCallback(() => {
    show({
      title: 'Ordering coming soon',
      body: 'Seed-store ordering stays behind the feature flag. Use Seed Stores in the toolkit for now.',
      variant: 'info',
    });
  }, [show]);

  return (
    <div className="plant-asst-shell plant-asst-shell--field" data-testid="planting-assistant-page">
      <ToolkitShellTabs />
      {!inFlow ? (
        <PlantingLibraryStrips
          seedOptions={seedOptions}
          plantingEvents={plantingEvents}
          savedGroups={savedGroups}
          plansReady={!groupsLoading}
          plantingEventsLoading={plantingEventsLoading}
          onOpenPlantingEvent={handleOpenPlantingEvent}
          onDeletePlantingEvent={handleAskDeletePlantingEvent}
        />
      ) : null}
      <div className="plant-asst-log-bar">
        <div
          className={`plant-asst-selection-chip${inFlow && selectedPolygons.length > 0 ? ' is-visible' : ''}`}
          aria-live="polite"
        >
          <span>
            {selectedPolygons.length} plot{selectedPolygons.length === 1 ? '' : 's'} selected
          </span>
          <span className="plant-asst-selection-chip__sep">|</span>
          <strong>{formatAcreage(selectedTotal)}</strong>
        </div>
        {inFlow ? (
          <p className="plant-asst-flow-kicker" data-testid="plant-flow-kicker">{flowKicker}</p>
        ) : null}
        {!inFlow ? (
          <YButton
            variant="primary"
            className="plant-asst-header-btn plant-asst-header-btn--enamel plant-asst-header-btn--accent"
            data-testid="plant-asst-draw-map-btn"
            onClick={() => navigate('/maps')}
          >
            <FaDrawPolygon aria-hidden="true" />
            <span className="plant-asst-header-btn__label">DRAW ON MAP</span>
          </YButton>
        ) : null}
        {inFlow && !isViewingPlan ? (
          <button
            type="button"
            className="plant-asst-start-over"
            data-testid="plant-asst-start-over"
            onClick={handleBackToLibrary}
          >
            START OVER
          </button>
        ) : null}
        {inFlow && calculatorMode === 'edit' && flowStep === 'plan' && (
          <YButton
            variant="primary"
            className="plant-asst-header-btn plant-asst-header-btn--enamel plant-asst-header-btn--accent"
            data-testid="plant-asst-save-group-btn"
            aria-label="Update area group"
            disabled={
              selectedPolygons.length === 0
              || (mode === 'club' && !selectedClubId)
              || editingSaveLoading
            }
            onClick={handleSaveEditGroup}
          >
            <FaSave aria-hidden="true" />
            <span className="plant-asst-header-btn__label">UPDATE AREA GROUP</span>
          </YButton>
        )}
      </div>

      {inFlow ? (
        <PlantingPlanStepStrip
          activeScreen={flowStep}
          onAllPlans={handleBackToLibrary}
          onStepSelect={handleStripStepSelect}
        />
      ) : null}

      <div className="plant-asst-inner">
        {!inFlow ? (
          <>
            <PlantingPlansLibrary
              savedGroups={savedGroups}
              groupsLoading={groupsLoading}
              groupError={groupError}
              selectedPlanIds={selectedPlanIds}
              onTogglePlanSelect={handleTogglePlanSelect}
              onStartNewPlan={handleStartNewPlan}
              onViewGroup={startViewingGroup}
              onEditGroup={startEditingGroup}
              onExportGroup={handleExportPlanFromCard}
              exportingPlanId={exportingPlanId}
              onShareGroup={handleShareGroup}
              onMoreGroup={handleMoreGroup}
              onMarkPlanted={handleMarkPlanted}
              onExportSelected={handleExportSelectedPlans}
              clubNames={clubNames}
            />
            <PlantingShareDialog
              group={shareGroup}
              show={Boolean(shareGroup)}
              onHide={() => setShareGroup(null)}
              onShared={handlePlanShared}
              onUnshared={handlePlanUnshared}
            />
            <PlantingMarkPlantedDialog
              group={markPlantedGroup}
              show={Boolean(markPlantedGroup)}
              onHide={() => setMarkPlantedGroup(null)}
              onSaved={handlePlantingLogged}
            />
            <PlantingMissingPlanDialog
              event={missingPlanEvent}
              show={Boolean(missingPlanEvent)}
              onHide={() => setMissingPlanEvent(null)}
            />
            <PlantingDeleteLogDialog
              event={pendingDeleteEvent}
              show={Boolean(pendingDeleteEvent)}
              deleting={deletingLogEvent}
              error={deleteLogError}
              onHide={() => {
                if (deletingLogEvent) return;
                setPendingDeleteEvent(null);
                setDeleteLogError('');
              }}
              onConfirm={handleConfirmDeletePlantingEvent}
            />
            {moreMenuGroup ? (
              <div className="plant-plans-more" data-testid="plant-plans-more-sheet">
                <p className="plant-plans-more__title">{moreMenuGroup.name}</p>
                <button
                  type="button"
                  className="plant-plans-card__btn"
                  onClick={() => {
                    handleViewOnMap(moreMenuGroup);
                    setMoreMenuGroupId(null);
                  }}
                >
                  VIEW ON MAP
                </button>
                <button
                  type="button"
                  className="plant-plans-card__btn"
                  disabled={deletingGroupId === moreMenuGroup.id}
                  onClick={() => {
                    handleDeleteGroup(moreMenuGroup.id);
                    setMoreMenuGroupId(null);
                  }}
                >
                  {deletingGroupId === moreMenuGroup.id ? 'DELETING…' : 'DELETE PLAN'}
                </button>
                <button
                  type="button"
                  className="plant-plans-card__btn"
                  onClick={() => setMoreMenuGroupId(null)}
                >
                  CANCEL
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <>
            {showPlotPicker ? (
              <>
                <PlantingGroundStep
                  acres={draftManualAcres}
                  onAcresChange={setDraftManualAcres}
                  placeQuery={draftPlaceQuery}
                  onPlaceQueryChange={handleGroundPlaceQueryChange}
                  selectedPlace={draftPlace}
                  onPlaceSelect={handleGroundPlaceSelect}
                  plots={activePolygons}
                  selectedIds={selectedIds}
                  onTogglePlot={handleGroundTogglePlot}
                  mode={mode}
                  selectedClubName={selectedClubName}
                  presidentClubs={presidentClubs}
                  selectedClubId={selectedClubId}
                  clubsLoading={clubsLoading}
                  onModeChange={handleGroundMapSourceChange}
                  onClubSelect={handleGroundClubSelect}
                  plotsLoading={loading || (mode === 'club' && clubPolygonsLoading)}
                  plotsError={error || (mode === 'club' ? clubPolygonsError : null)}
                  draftZoneCode={draftZoneCode}
                  draftZoneSource={draftZoneSource}
                  draftSoilType={draftSoilType}
                  draftSoilTypeLabel={groundSoilTypeLabel}
                  draftSoilTypeId={draftSoilType}
                  draftSoilTypeSource={draftSoilTypeSource}
                  draftSoilDrainage={draftSoilDrainage}
                  draftSoilDrainageLabel={groundSoilDrainageLabel}
                  draftSoilDrainageSource={draftSoilDrainageSource}
                  draftSoilPh={draftSoilPh}
                  draftSoilPhSource={draftSoilPhSource}
                  draftSoilSeries={draftSoilSeries}
                  draftSoilDrainageRaw={draftSoilDrainageRaw}
                  draftFrostLastLabel={draftFrostLastLabel}
                  draftFrostFirstLabel={draftFrostFirstLabel}
                  draftFrostSource={draftFrostSource}
                  soilSurveyCovered={draftSoilSurveyCovered}
                  soilLookupLoading={detectZoneLoading}
                  zoneOptionsList={zoneOptionsList}
                  soilTypeOptions={soilTypeOptions}
                  soilDrainageOptions={soilDrainageOptions}
                  onZoneOverride={handleGroundZoneOverride}
                  onSoilTypeOverride={handleGroundSoilTypeOverride}
                  onSoilDrainageOverride={handleGroundSoilDrainageOverride}
                  onSoilPhOverride={handleGroundSoilPhOverride}
                  onFrostOverride={handleGroundFrostOverride}
                  onResetToSurvey={handleGroundResetToSurvey}
                  planName={draftGroupName}
                  onPlanNameChange={(value) => {
                    setDraftGroupName(value);
                    if (hasPlanName(value)) setPlanNameNeeded(false);
                  }}
                  nameNeeded={planNameNeeded}
                  readOnly={isViewingPlan}
                />
                <div className="plant-flow-cta" data-testid="plant-flow-cta">
                  <button
                    type="button"
                    className="plant-flow-cta__btn"
                    disabled={isViewingPlan ? false : groundContinueDisabled}
                    onClick={handleStickyFlowCta}
                  >
                    {continueLabelForStep('ground', { viewing: isViewingPlan })}
                  </button>
                </div>
              </>
            ) : null}

            {flowStep === 'wizard' ? (
              <>
                <PlantingWizardStep
                  answers={draftWizardAnswers}
                  questionIndex={draftWizardIndex}
                  onSelectOption={handleWizardSelectOption}
                  onSelectQuestion={handleWizardSelectQuestion}
                  readOnly={isViewingPlan}
                  place={groundPlace}
                />
                <div className="plant-flow-cta" data-testid="plant-flow-cta">
                  <button
                    type="button"
                    className="plant-flow-cta__btn"
                    disabled={isViewingPlan ? false : wizardContinueDisabled}
                    onClick={handleStickyFlowCta}
                  >
                    {continueLabelForStep('wizard', { wizardLabel: wizardCtaLabel, viewing: isViewingPlan })}
                  </button>
                </div>
              </>
            ) : null}

            {flowStep === 'reveal' ? (
              <>
                <PlantingRevealStep
                  place={groundPlace}
                  draftSoilPh={draftSoilPh}
                  draftSoilDrainageLabel={groundSoilDrainageLabel || draftSoilDrainageRaw}
                  draftSoilTypeId={draftSoilType}
                  onStageChange={handleRevealStageChange}
                />
                <div className="plant-flow-cta" data-testid="plant-flow-cta">
                  <button
                    type="button"
                    className="plant-flow-cta__btn"
                    disabled={isViewingPlan ? false : revealContinueDisabled}
                    onClick={handleStickyFlowCta}
                  >
                    {continueLabelForStep('reveal', { revealLabel: revealCtaLabel, viewing: isViewingPlan })}
                  </button>
                </div>
              </>
            ) : null}

            {showPrototypePlan ? (
              <>
                <PlantingPlanStep
                  answers={draftWizardAnswers}
                  place={groundPlace}
                  acres={selectedTotal}
                  draftSoilPh={draftSoilPh}
                  onOrderClick={handlePlanOrderClick}
                  preferKey={draftBlendKey}
                  onSelectAlt={isViewingPlan ? undefined : setDraftBlendKey}
                  readOnly={isViewingPlan}
                  loadPlan={toolkitService.getPlanPreview}
                  onPreview={setDraftPlanPreview}
                />
                <div className="plant-flow-cta" data-testid="plant-flow-cta">
                  <button
                    type="button"
                    className="plant-flow-cta__btn"
                    onClick={handleStickyFlowCta}
                  >
                    {continueLabelForStep('plan', { viewing: isViewingPlan })}
                  </button>
                </div>
              </>
            ) : null}

            {showLegacyPlanCalculator ? (
              <>
                <PlantingPlanFlow
                  selectedPolygons={selectedPolygons}
                  selectedTotal={selectedTotal}
                  seedOptions={seedOptions}
                  fertilizerOptions={fertilizerOptions}
                  selectedSeedId={selectedSeedId}
                  selectedFertilizerId={selectedFertilizerId}
                  onSeedSelect={handleSeedSelect}
                  onFertilizerSelect={handleFertilizerSelect}
                  recommendationResult={recommendationResult}
                  recommendationLoading={recommendationLoading}
                  recommendationError={recommendationError}
                  onSaveEstimate={handleSaveFromCalculator}
                  onGoToPlots={() => goToFlowStep('ground')}
                  onEditGroup={handleEditFromView}
                  calculatorMode={calculatorMode}
                  saveEstimateDisabled={
                    selectedPolygons.length === 0
                    || (mode === 'club' && !selectedClubId)
                    || !draftGroupName.trim()
                    || (draftZoneSource === 'manual' && !draftZoneCode)
                    || (!selectedSeedId && !selectedFertilizerId)
                  }
                  saveEstimateLoading={savingGroup}
                  canExportPdf={canExportPdf}
                  exportPdfLoading={exportPdfLoading}
                  onExportPdf={handleExportPdf}
                  draftGroupName={draftGroupName}
                  onDraftGroupNameChange={setDraftGroupName}
                  draftGroupNote={draftGroupNote}
                  onDraftGroupNoteChange={setDraftGroupNote}
                  editingZoneLabel={editingZoneLabel}
                  editingManualOverride={editingManualOverride}
                  onDraftManualToggle={handleDraftManualToggle}
                  draftZoneCode={draftZoneCode}
                  onDraftZoneCodeChange={(value) => {
                    setDraftZoneCode(value);
                    if (recommendationResult) {
                      setAutoRecalcQueued(true);
                    }
                  }}
                  zoneOptionsList={zoneOptionsList}
                  formatZoneOptionLabel={formatZoneOptionLabel}
                  optionsLoading={optionsLoading}
                  draftSoilType={draftSoilType}
                  onDraftSoilTypeChange={(value) => {
                    setDraftSoilType(value);
                    if (recommendationResult) {
                      setAutoRecalcQueued(true);
                    }
                  }}
                  soilTypeOptions={soilTypeOptions}
                  draftSoilDrainage={draftSoilDrainage}
                  onDraftSoilDrainageChange={(value) => {
                    setDraftSoilDrainage(value);
                    if (recommendationResult) {
                      setAutoRecalcQueued(true);
                    }
                  }}
                  soilDrainageOptions={soilDrainageOptions}
                  draftSoilPh={draftSoilPh}
                  onDraftSoilPhChange={(value) => {
                    setDraftSoilPh(value);
                    if (recommendationResult) {
                      setAutoRecalcQueued(true);
                    }
                  }}
                  phHelperText={phHelperText}
                  soilPhGuidanceBlurb={groupOptions.soilPhGuidance?.blurb}
                  draftSoilNotes={draftSoilNotes}
                  onDraftSoilNotesChange={setDraftSoilNotes}
                  soilNotePlaceholder={soilNotePlaceholder}
                  optionsError={optionsError}
                  detectZoneLoading={detectZoneLoading}
                  detectZoneError={detectZoneError}
                  onDetectZone={handleDetectZone}
                  editingGroupId={editingGroupId || calculatorGroupId}
                  saveError={calculatorMode === 'compose' ? saveError : editingError}
                  onCancelEditGroup={handleCancelEditGroup}
                />
                <div className="plant-flow-cta" data-testid="plant-flow-cta">
                  <button
                    type="button"
                    className="plant-flow-cta__btn"
                    onClick={handleContinueFlow}
                  >
                    {continueLabelForStep('plan')}
                  </button>
                </div>
              </>
            ) : null}

            {flowStep === 'timing' ? (
              <>
                <PlantingTimingStep
                  answers={draftWizardAnswers}
                  cropName={planCropName}
                  latitude={timingPoint?.latitude ?? null}
                  longitude={timingPoint?.longitude ?? null}
                  usdaZoneCode={draftZoneCode}
                  soilDrainage={draftSoilDrainage}
                  frostSource={draftFrostSource}
                  frostLastLabel={draftFrostLastLabel}
                  frostFirstLabel={draftFrostFirstLabel}
                />
                <div className="plant-flow-cta" data-testid="plant-flow-cta">
                  <button
                    type="button"
                    className="plant-flow-cta__btn"
                    onClick={handleStickyFlowCta}
                  >
                    {continueLabelForStep('timing', { viewing: isViewingPlan })}
                  </button>
                </div>
              </>
            ) : null}

            {flowStep === 'growth' ? (
              <>
                <PlantingGrowthStep
                  answers={draftWizardAnswers}
                  place={groundPlace}
                  acres={selectedTotal}
                  saved={planSaved}
                  preferKey={draftBlendKey}
                  draftSoilPh={draftSoilPh}
                  preview={draftPlanPreview}
                  loadPlan={toolkitService.getPlanPreview}
                />
                <div className="plant-flow-cta" data-testid="plant-flow-cta">
                  <button
                    type="button"
                    className="plant-flow-cta__btn"
                    disabled={isViewingPlan ? false : savingGroup}
                    onClick={handleStickyFlowCta}
                  >
                    {continueLabelForStep('growth', { growthLabel: growthCtaLabel, viewing: isViewingPlan })}
                  </button>
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
